export const API_KEY_DOCS = [
  {
    id: "agents-md",
    title: "AGENTS.md",
    filename: "AGENTS.md",
    audience: "Agent",
    summary: "Codex / Claude Codeが最初に読むワークスペース作法。",
    content: `# AGENTS.md

## Workspace Identity

This folder is the JAPAN GTM Agent Workspace powered by SalesBreaker.

Use it when Codex, Claude Code, Cursor, ChatGPT Agent, or another trusted AI agent helps a logged-in SalesBreaker user operate Japan go-to-market workflows.

SalesBreaker is the operating system. The workspace is the local contract pack that tells the agent how to use SalesBreaker safely.

## First Things To Do

1. Read \`START_HERE.md\`.
2. Read \`workspace-version.json\`.
3. Confirm this local workspace is current before operating SalesBreaker.
4. If the local workspace is stale, stop and ask the user to download the latest ZIP from the SalesBreaker API Key page.

## Operating Rules

- Use browser assist when the SalesBreaker screen is the safest interface.
- Use API assist when a documented Operator API route is available and safer than UI work.
- Treat the Agent role as preparation, analysis, recording, drafting, and setup assistance.
- Use API routes for read/analyze work and for confirmed assist writes such as company profile, NG settings, exclusion times, templates, and activity logs.
- Keep send, publish, activate, invoice, contract, e-sign, payment, and DNS final checks as human/browser confirmation points.
- Do not treat user management, permissions, billing, API key issuance, or admin-only settings as normal Agent operation targets.
- For Directory-safe API operation, use \`operator.read.directory_v1\` / \`operator.assist.directory_v1\`.
- For normal SaaS API operation, \`operator.read\` / \`operator.assist\` may expose broader tenant tools, but My Lists write still requires the SalesBreaker screen unlock gate and attestation.
- Never pass \`client_id\` in API-key request body or query. SalesBreaker resolves the tenant from the API key.
- Never store raw API keys, Authorization headers, secrets, or giant target ID arrays.
- Preserve working notes in \`strategy/\`, \`campaigns/\`, \`saved-lists/\`, \`templates/\`, \`analytics/\`, \`reports/\`, and \`logs/\`.
- Preview before save, publish, delete, bulk update, send preparation, or send execution.
- Ask for human confirmation before save, publish, delete, bulk update, send execution, billing changes, or user-management changes.

## Domain Readiness

- \`salesbreaker.jp\` is the SaaS app, login, and API domain. Do not assume it is the tracking domain for outgoing messages.
- Click tracking, open tracking, and unsubscribe URLs should use the current tracking domain.
- Default tracking domain: \`track.sbroute.net\`.
- Custom tracking domain: use \`track.client-domain.com\` only when DNS, SSL, and backend route checks are ready.
- Sender DNS and tracking DNS are separate. Current basic client DNS setup is DKIM CNAME x3 plus tracking CNAME x1.
- Before first send or delivery troubleshooting, inspect company profile, sender readiness, tracking readiness, and send exclusion settings.
- If DNS or hosting console access is required, ask the user which server/domain provider they use and provide precise records to add. Do not invent DNS state.

## Send Safety

Sending is allowed only through the current SalesBreaker-approved workflow for the user's plan, API key, target list, template, speed/slot selection, preview, and human approval.

Do not invent send routes. Do not bypass preview, confirmation, idempotency, audit, or quota/speed checks.

## Practical Screen Rules

- Company profile and NG settings: fill company/sender fields first. Missing phone, address, staff name, or email can break forms with required fields. Use a purpose-specific sender address; free mail is discouraged.
- Templates: keep copy short and campaign-specific. Add one clear LP, material, webinar, article, or scheduling URL when useful.
- My Lists and recipient lists: clean inputs, prefer structured JSON when API write is allowed, preview count/sample/exclusions, and never treat list creation as send execution.
- History, clicks, tracking, and AI inflow: analyze and join with CRM, CSV, ecommerce, or form data before claiming conversion or revenue impact.
- Deals, leads, and CRM: notes, follow-up tasks, and status-change drafts are valid Agent work; billing, contracts, electronic signatures, and payment actions require human review.
- Email, scenarios, forms, LPs, documents, and marketing settings: draft/setup through API or browser assist; publish, activate, send, or externally verify only after human confirmation.

## Goal

Help the user run Japan GTM work with better preparation, better targeting, better copy, better analysis, and better follow-up.

Do not treat SalesBreaker as a magic appointment generator. Inspect setup, target quality, message length, URLs/LPs, volume, timing, clicks, CRM follow-up, and next actions before judging performance.
`,
  },
  {
    id: "start-here",
    title: "START_HERE",
    filename: "START_HERE.md",
    audience: "Agent",
    summary: "Agentが最初に読む最新版確認と安全ルール。",
    content: `# START_HERE

This workspace is the local operating pack for Codex, Claude Code, Cursor, ChatGPT Agent, and trusted AI agents working on Japan GTM with SalesBreaker.

Think of it as:

\`\`\`text
JAPAN GTM Agent Workspace
powered by SalesBreaker
\`\`\`

SalesBreaker is the sales operating system. This workspace is the local manual, JSON contract pack, safety policy, and working folder structure that tells an agent how to operate it.

## First Rule

Before every serious SalesBreaker operation, verify that this local workspace is current.

1. Open the SalesBreaker \`APIキー / AI連携\` page.
2. Check the latest JAPAN GTM Agent Workspace ZIP version shown or downloaded from that page.
3. Compare it with \`workspace-version.json\` in this folder.
4. If the local version is older, download the latest ZIP and replace this workspace before continuing.

Do not rely on stale local Markdown when deciding which route, JSON shape, permission, or safety rule to use.

If the user only says what they want to accomplish, translate that goal into a SalesBreaker operation plan. Do not force the user to choose API routes manually.

## Operation Boundary

The Agent's normal role is preparation, analysis, recording, drafting, and setup assistance. Final execution stays with the human.

| Class | Agent can do | Human confirmation |
| --- | --- | --- |
| API read / analyze | Read settings, history, clicks, AI inflow, deals, customers, leads, and summarize or join data | Usually not required |
| API draft / assist write | Draft or update company profile, NG settings, exclusion times, templates, notes, and setup plans | Required before save, delete, bulk update, or status-changing writes |
| Browser confirm / execute | Send, publish, activate, issue invoices, generate/send contracts, confirm DNS, and finalize billing-sensitive work | Always required |
| Out of normal scope | User management, permissions, billing, API key issuance, and admin-only settings | Do not operate by default |

When a route is API-key ready, it still requires \`Authorization: Bearer <salesbreaker_api_key>\`. Do not treat API-key access as public unauthenticated access.

## Auth Surfaces

Use the correct auth surface for each route.

\`\`\`json
{
  "api_key_ready": {
    "auth": "Authorization: Bearer <salesbreaker_api_key>",
    "use_for": ["/api/v1/*", "/api/operator/v0/*"],
    "client_id_source": "resolved_from_api_key"
  },
  "browser_assist_ok": {
    "auth": "logged-in SalesBreaker browser session or login JWT",
    "use_for": ["existing browser screen routes"],
    "client_id_source": "resolved_from_login_session"
  },
  "browser_jwt_required": {
    "auth": "Authorization: Bearer <login_jwt>",
    "use_for": ["/api/marketing/*", "/api/docs/*", "/api/issuer-profiles", "/api/client-info", "/api/usage-status"],
    "client_id_source": "resolved_from_login_session"
  }
}
\`\`\`

Never pass \`client_id\` in request body or query for API-key routes. SalesBreaker resolves the tenant from the API key.

Directory-safe API keys should use \`operator.read.directory_v1\` / \`operator.assist.directory_v1\`. Normal SaaS users may use \`operator.read\` / \`operator.assist\`, but My Lists write still requires the same paid/DNS/sender/attestation unlock gate as the SalesBreaker screen.

## Current Public API-Key Ready Routes

Use an API key with the required permission bundle. Existing API keys that already include \`operator.read\` / \`operator.assist\` can use newly added routes when the server-side bundle includes them. If a key does not include the required bundle, issue a new key.

\`\`\`text
GET  /api/operator/v0/help/context
GET  /api/operator/v0/help/capabilities
GET  /api/operator/v0/help/safety
GET  /api/operator/v0/help/examples
GET  /api/operator/v0/dashboard/summary
GET  /api/operator/v0/account/status
GET  /api/operator/v0/settings/read
POST /api/operator/v0/templates/draft
POST /api/operator/v0/templates/preview
POST /api/operator/v0/templates/save-preview
POST /api/operator/v0/analytics/diagnose
POST /api/operator/v0/saved-lists/preview
POST /api/operator/v0/engagement/search
GET  /api/operator/v0/tracking/summary
POST /api/operator/v0/tracking/events/search
POST /api/operator/v0/tracking/ai-discovery/search
POST /api/operator/v0/tracking/visitors/search
POST /api/operator/v0/history/search
POST /api/operator/v0/deals/search
POST /api/operator/v0/deals/context
POST /api/operator/v0/deals/activities/search
POST /api/operator/v0/customers/search
POST /api/operator/v0/customers/context
\`\`\`

## Current Public Assist Routes

Use an API key with \`operator.assist.directory_v1\` for Directory-safe assist, or \`operator.assist\` for normal SaaS assist. Do not pass \`client_id\`; SalesBreaker resolves it from the key.

\`\`\`text
POST /api/operator/v0/settings/company-profiles/upsert
POST /api/operator/v0/settings/company-profiles/delete
POST /api/operator/v0/settings/ng-list/upsert
POST /api/operator/v0/settings/ng-list/bulk-upsert
POST /api/operator/v0/settings/ng-list/delete
POST /api/operator/v0/settings/excluded-settings/upsert
POST /api/operator/v0/templates/save
POST /api/operator/v0/templates/delete
POST /api/operator/v0/activities/log
\`\`\`

My Lists / arbitrary imported house-list routes are not public in the Directory-safe English/Codex-facing workspace.
Normal SaaS My Lists API access is conditional: paid subscription active, trial finished, verified email, sender/DKIM readiness, custom tracking domain readiness, attestation, and human confirmation.
Use \`POST /api/operator/v0/saved-lists/preview\` for controlled SalesBreaker/JAPAN GTM recipient-list review.

## Domain Readiness Check

Before first send, delivery troubleshooting, or English/OEM domain planning, check the client's domain state.

\`\`\`json
{
  "app_domain": {
    "purpose": "SalesBreaker SaaS, login, API",
    "default": "salesbreaker.jp"
  },
  "tracking_domain": {
    "purpose": "click tracking, open tracking, unsubscribe URLs",
    "default": "track.sbroute.net",
    "custom_priority": "client custom tracking domain only when DNS, SSL, and backend route are ready",
    "do_not_assume": "salesbreaker.jp"
  },
  "sender_dns": {
    "purpose": "sender authentication",
    "current_basic_records": ["DKIM CNAME x3"],
    "separate_from_tracking": true
  },
  "client_dns_records_to_show": [
    "DKIM CNAME x3",
    "track CNAME x1"
  ],
  "agent_before_send_check": [
    "company profile completeness",
    "sender profile and verification readiness",
    "tracking current_domain/source/readiness",
    "send exclusion settings",
    "template URL or LP destination"
  ]
}
\`\`\`

If the user asks "I created a project, what should I do next?", guide them through company profile, sender/DNS, tracking CNAME, one-tag, template, NG/exclusion settings, recipient list, preview, human approval, and later analysis.

## Practical Screen Rules

- Company profile and NG settings: fill required sender/company fields before judging send failures. End forms often require phone, address, staff name, and email. Use a purpose-specific sender/staff address; free mail addresses are discouraged.
- Templates: keep copy short. Add a concrete LP, material, webinar, article, or scheduling URL when it supports the campaign goal. Awareness-only copy without a URL is allowed only when the intent is explicit.
- My Lists: clean user-owned lists before import. When API write access is available, structured JSON import is safer than fragile CSV column alignment.
- Recipient lists: build by conditions, preview count/sample/exclusions, then save only after confirmation. Creating a list is not sending.
- Sending: prepare and preview through API or browser assist, but the actual send button is a human confirmation point.
- History, clicks, tracking, AI inflow: analyze through read routes and join with CRM, CSV, ecommerce, or form data before claiming revenue impact.
- Deals, leads, CRM notes: logging and status-change drafts are useful Agent work. Invoice, contract, e-sign, payment, and order finalization require human browser review.
- Email, scenarios, forms, LPs, documents, and marketing settings: Agent may draft/setup; publish, activate, deliver, or externally verify only after human review.
- Account, user, billing, permissions, and API key administration are not normal Agent operation targets.

## Browser Assist Is Still Valid

Do not force everything through API routes.

If a screen contract says \`browser_assist_ok\` or \`not_yet_api_key_enabled\`, use browser assist for that screen. Browser assist is an intended path, especially for complex UI workflows.

## Safety

Preview before save, publish, delete, bulk update, or send execution.

Human confirmation is required before any operation that changes live data, publishes content, sends messages, deletes records, or changes billing/user settings.

Send execution is a separate gated workflow. Do not execute send routes unless the current workspace and SalesBreaker screen explicitly say the route is available for the current plan, key, target list, and human approval.
`,
  },
  {
    id: "workspace-version",
    title: "Workspace Version",
    filename: "workspace-version.json",
    audience: "Agent",
    summary: "JAPAN GTM Agent Workspace ZIPの版管理情報。",
    content: `{
  "workspace_name": "JapanGtmAgentWorkspacePoweredBySalesBreaker",
  "display_name": "JAPAN GTM Agent Workspace powered by SalesBreaker",
  "version": "2026-06-12T10:20:00+0900",
  "minimum_required_for": [
    "saas_api_key_operator_read_routes",
    "saas_api_key_operator_assist_routes",
    "agent_frontend_coverage",
    "agent_contract_pack",
    "agents_md",
    "openai_directory_phase0_asset_protection",
    "normal_saas_tracking_read_routes",
    "agent_operation_boundary_rules"
  ],
  "download_url": "https://salesbreaker.jp/app/downloads/salesbreaker-agent-workspace.zip",
  "check_latest_before_every_run": true,
  "api_key_page_path": "/app/api-keys",
  "notes": [
    "If this local version is older than the API key page download, replace the workspace before operating SalesBreaker.",
    "Existing API keys that include operator.read or operator.assist can use server-side bundle additions. Reissue keys that do not include the required bundle."
  ]
}
`,
  },
  {
    id: "overview",
    title: "Overview",
    filename: "salesbreaker-api-overview.md",
    audience: "Client",
    summary: "SalesBreaker APIキーとAI連携の全体像。",
    content: `# JAPAN GTM Agent Workspace Overview

Powered by SalesBreaker.

SalesBreaker APIキーは、外部システム、エンジニア、AIツールがSalesBreakerの情報取得、確認、下書き、送信前確認、分析、CRMフォローを行うための認証キーです。

CRM連携、リスト品質確認、AI操作用の導線は同じAPIキー体系で扱います。

## 利用イメージ

- CRM/BI連携: 送信履歴、クリック反応、案件情報を外部ツールへ連携する。
- リスト品質確認: 企業リストの品質、スコア、推奨アクションを確認する。
- AI操作: Codex / Claude Code / Cursor / ChatGPT AgentなどがSalesBreaker全体の確認、下書き、送信前確認、分析、CRMフォローを支援する。
- Japan GTM運用: 日本市場向けのターゲット選定、営業戦略、リスト整理、文面改善、LP/資料作成、反応分析、CRMフォローを1つのワークスペースで進める。

## 公開範囲

主要フロント操作、情報確認、下書き、送信前確認、分析、CRMフォローを公開対象として扱います。

フォーム送信、メール配信、シナリオ配信などの実行系は、無料プランでは直接実行できません。有料プラン、送信枠、プレビュー、人間承認、重複実行防止、監査ログを通して扱います。

## JAPAN GTM Agent Workspaceの更新確認

Codex / Claude CodeなどのAgentに作業させる前に、必ずAPIキー画面から最新のJAPAN GTM Agent Workspace ZIPを確認してください。

ローカルに解凍済みのWorkspaceが古い場合、Agentは古いroute、古いJSON contract、古い権限情報を前提に操作してしまいます。

Workspace内の \`workspace-version.json\` と、APIキー画面から取得できる最新ZIPを比較し、古い場合は再ダウンロードしてから作業を開始してください。

## Domain Readiness / 初期設定

Agentは、送信前やCS初期設定時に以下を確認してください。

- \`salesbreaker.jp\` はSaaS本体、ログイン、API用のドメインです。
- クリック計測、開封計測、配信停止URLはtracking domainを使います。
- DNS未設定時の標準tracking domainは \`track.sbroute.net\` です。
- 顧客custom tracking domainは、DNS、SSL、backend routeがOKのときだけ使います。
- 現在の基本DNS案内は DKIM CNAME 3件 + tracking CNAME 1件です。
- Sender DNSとTracking DNSは別物として扱います。

新規プロジェクトの初期設定では、会社概要、送信元/DNS、tracking domain、1行タグ、文面テンプレート、NG/送信除外時間、送付先リスト、送信前preview、48時間後の反応分析までを一連の運用として案内してください。
`,
  },
  {
    id: "authentication",
    title: "Authentication",
    filename: "salesbreaker-authentication.md",
    audience: "Client",
    summary: "APIキー認証、client_id、display-once/hash保存の扱い。",
    content: `# Authentication

APIキーはSalesBreakerのAPIキー画面で発行します。APIキーの実値は発行時に一度だけ表示され、SalesBreaker側には復元できない形式で保存されます。

## Header

\`\`\`bash
Authorization: Bearer <salesbreaker_api_key>
\`\`\`

APIキーはURLのquery stringに入れないでください。ブラウザ履歴、アクセスログ、外部ツールのログに残る可能性があります。

## APIキーrouteとブラウザ操作route

SalesBreakerには、APIキーで呼び出すrouteと、ログイン済みブラウザ操作で使うrouteがあります。

\`\`\`json
{
  "api_key_ready": {
    "auth": "Authorization: Bearer <salesbreaker_api_key>",
    "examples": ["/api/v1/crm/*", "/api/v1/sales-list/*", "/api/operator/v0/*"]
  },
  "browser_assist_ok": {
    "auth": "Authorization: Bearer <login_jwt>",
    "examples": ["/api/company-profiles", "/api/marketing/*", "/api/docs/*", "/api/issuer-profiles", "/api/client-info", "/api/usage-status"],
    "note": "These routes are for logged-in browser operation. Prefer /api/operator/v0/* assist routes for terminal-safe API-key work when available."
  }
}
\`\`\`

## client_id

\`client_id\` は秘密情報ではありません。ただし、どの会社のデータを読めるかはAPIキーから自動判定します。リクエストbodyやqueryに指定された \`client_id\` は信用しません。

## キーの扱い

- APIキーの実値は発行時に一度だけ表示されます。
- 一覧・詳細・利用ログではAPIキーの実値を再表示しません。
- 不要になったキーは停止または無効化できます。
- 停止・無効化などの操作は監査ログに残します。`,
  },
  {
    id: "permissions",
    title: "Permissions",
    filename: "salesbreaker-permissions.md",
    audience: "Client",
    summary: "無料/有料で利用できるAPI権限と送信系の扱い。",
    content: `# Permissions

APIキーには利用権限を設定します。無料プランでは情報取得・内容確認を中心に、有料プランでは確認付きの実行系を扱います。

## 初期公開の利用権限

| 利用権限 | できること | 初期方針 |
| --- | --- | --- |
| CRMデータ取得 | 送信履歴、クリック反応、案件情報の取得 | 無料プラン候補 |
| リスト品質確認 | 企業リストの品質・スコア確認 | 無料プラン候補 |
| AIツール向け確認 | AIツールによる情報確認・診断 | 無料プラン候補 |
| AI操作補助 | 会社概要、NG設定、文面テンプレート、管理済み送付先リストの確認、活動ログの整理・保存 | 無料プラン候補 |
| 送信実行 | 送付先リストと文面テンプレートを確定した後の承認付き送信 | 有料プラン限定 |

## 有料プランで扱う確認付き実行

以下は外部への送信や一括処理を伴うため、無料プランでは直接実行できません。

- フォーム送信の承認付き実行
- メール配信の承認付き実行
- シナリオ配信の承認付き開始
- 一括操作や大量処理

送信数だけが問題ではありません。APIやAIツールから実行できること自体が強い権限なので、有料プラン、送信枠、送信前プレビュー、人間承認、監査ログを前提にします。

\`campaign.execute\` は直接検索して即送信する権限ではありません。送付先リスト、文面テンプレート、対象件数、送信速度、除外/NG/重複リスクを確認した後にだけ使います。

\`operator.assist\` は送信以外のSalesBreaker操作補助です。保存、削除、一括変更には人間確認が必要です。請求、ユーザー管理、送信実行は含みません。

Directory / public listing向けには \`operator.read.directory_v1\` / \`operator.assist.directory_v1\` を使います。この2つにはMy Lists write、Saved Lists write、unrestricted company search、send executionを含めません。

通常SaaS向けの \`operator.read\` / \`operator.assist\` は維持します。通常SaaS向けにはAI流入・Web計測の読み取りも含みます。ただしMy Lists writeはscopeだけでは実行できず、SaaS画面と同じunlock gate / attestation gateを通過したアカウントだけが使えます。`,
  },
  {
    id: "crm-export-api",
    title: "CRM Export API",
    filename: "salesbreaker-crm-export-api.md",
    audience: "Client",
    summary: "CRM/案件/送信履歴を外部ツールへ連携するAPI。",
    content: `# CRM Export API

CRM Export APIは、SalesBreaker内の送信履歴、クリック反応、案件情報を外部CRM・BIツールへ連携するためのAPIです。

## 主な用途

- 送信履歴を外部CRMへ同期する。
- クリック反応やフォーム到達状況をBIで分析する。
- 案件や顧客の状況を外部レポートへ反映する。

## 境界

- 情報取得・連携用です。
- メール送信やフォーム送信は行いません。
- 案件や顧客情報を更新しません。

## エンドポイント

いずれも \`Authorization: Bearer sb_live_...\`（scope bundle: \`crm.read\`）。\`limit\` は既定500・最大1000、\`since\`（ISO8601）と \`cursor\`（前回応答の \`next_cursor\`）でページングします。

- \`GET /api/v1/crm/targets\` — 送信対象（企業）一覧
- \`GET /api/v1/crm/send-history\` — 送信履歴（1送信=1行）
- \`GET /api/v1/crm/click-events\` — クリックイベント生データ（1クリック=1行、timestamp付き）

### 例: クリックイベントを取得する

\`\`\`bash
curl -s "https://salesbreaker.jp/api/v1/crm/click-events?limit=500" \\
  -H "Authorization: Bearer sb_live_xxxxxxxx"
\`\`\`

クリックの集約分析（企業単位のロールアップ・商談文脈付き）は \`POST /api/operator/v0/engagement/search\`（bundle: \`operator.read\`）を使います。生イベントの全量が必要な場合だけ本APIを使ってください。

既存のCRM連携APIは段階的に新しいSalesBreaker APIキー体系へ寄せます。`,
  },
  {
    id: "sales-list-quality-api",
    title: "Sales List Quality API",
    filename: "salesbreaker-sales-list-quality-api.md",
    audience: "Client",
    summary: "企業リストの品質、スコア、推奨アクションの確認。",
    content: `# Sales List Quality API

Sales List Quality APIは、企業リストの品質、スコア、推奨アクションを確認するためのAPIです。

## 主な用途

- 送付先リストの品質を事前に確認する。
- 問い合わせURLや企業URLの有無を確認する。
- スコアやグレードをもとに、優先して送る対象を選ぶ。
- NG、重複、直近送信済みなどの除外理由を確認する。

## 対象の表し方

外部境界では、対象企業を \`master:{id}\` または \`client:{id}\` の形式で扱います。

\`sales_list_id\` のような内部IDを単体で外部ツールへ渡す運用は避けます。

## 送信前確認との関係

リスト内容は後から変わる可能性があります。送信前には件数、照合情報、スコア版、クレンジング版、リスト作成版を再確認し、ズレがあれば新しい確認を作り直します。`,
  },
  {
    id: "operator-api",
    title: "Operator API",
    filename: "salesbreaker-operator-api.md",
    audience: "Client / Agent",
    summary: "AIツールがSalesBreakerを確認・診断するためのAPI。",
    content: `# Operator API

Operator APIは、Codex / Claude CodeなどのAIツールがSalesBreaker全体の業務を安全に扱うためのAI操作層です。

## 認証

APIキーでOperator APIを使う場合は、用途に応じたscope bundleを選びます。

- Directory / public listing向け: \`operator.read.directory_v1\` / \`operator.assist.directory_v1\`
- 通常SaaS本体向け: \`operator.read\` / \`operator.assist\`

Directory向けbundleはread / preview / planning中心です。My Lists write、Saved Lists write、unrestricted company search、send executionは含みません。

\`\`\`bash
Authorization: Bearer <salesbreaker_api_key>
\`\`\`

SalesBreakerの既存画面はブラウザ操作でも扱えます。Agentがターミナルから安全に操作したい場合は、下記のOperator API routeを使ってください。

## Agent operation boundary

Operator APIは、Agentが準備、分析、記録、下書き、設定補助を行うための作業口です。送信、公開、シナリオ有効化、請求、契約書、電子契約、決済、DNS最終確認、ユーザー管理、権限変更、APIキー発行を自動確定するためのものではありません。

保存、削除、一括変更、ステータス変更、配信対象反映など副作用のある操作は、previewと人間確認を挟んでください。実送信や実配信は、ブラウザ上の確認、プラン/枠、送信前preview、人間承認、監査ログを通して扱います。

## 初期公開で想定すること

- 会社概要、NG設定、アカウント状態を確認する。
- 企業リストや送付先リストの内容を確認する。
- 文面テンプレートの下書きや送信前確認を支援する。
- 送信履歴、クリック反応、案件・顧客状況を確認する。
- 1行タグ、AI流入、匿名訪問者の傾向を確認する。
- リード、メール配信、シナリオ、フォーム、LP、資料、マーケティング設定の準備を支援する。
- 次に確認すべきポイントを整理する。

## Directory v1で利用できるread / preview route

\`operator.read.directory_v1\` を含むAPIキーで利用します。\`client_id\` はAPIキーから解決されるため、body/queryに指定しないでください。

\`\`\`text
GET  /api/operator/v0/help/context
GET  /api/operator/v0/help/capabilities
GET  /api/operator/v0/help/safety
GET  /api/operator/v0/help/examples
GET  /api/operator/v0/dashboard/summary
GET  /api/operator/v0/account/status
GET  /api/operator/v0/settings/read
POST /api/operator/v0/templates/draft
POST /api/operator/v0/templates/preview
POST /api/operator/v0/templates/save-preview
POST /api/operator/v0/analytics/diagnose
POST /api/operator/v0/saved-lists/preview
POST /api/operator/v0/engagement/search
POST /api/operator/v0/history/search
POST /api/operator/v0/deals/search
POST /api/operator/v0/deals/context
POST /api/operator/v0/deals/activities/search
POST /api/operator/v0/customers/search
POST /api/operator/v0/customers/context
\`\`\`

Directory v1では以下を含めません。

\`\`\`text
POST /api/operator/v0/companies/search
POST /api/operator/v0/my-lists/search
POST /api/operator/v0/my-lists/upsert
POST /api/operator/v0/my-lists/delete
POST /api/operator/v0/saved-lists/search
POST /api/operator/v0/saved-lists/save
POST /api/operator/v0/saved-lists/delete
GET  /api/operator/v0/tracking/summary
POST /api/operator/v0/tracking/events/search
POST /api/operator/v0/tracking/ai-discovery/search
POST /api/operator/v0/tracking/visitors/search
\`\`\`

Directory v1ではtracking / AI inflow read routeを含めません。

## 通常SaaS APIキーで利用できるread / preview route

\`operator.read\` を含むAPIキーで利用します。\`client_id\` はAPIキーから解決されるため、body/queryに指定しないでください。既存APIキーでも \`operator.read\` / \`operator.assist\` bundleを持つものは、サーバー側bundleに追加されたread routeを利用できます。該当bundleがない場合は新しいAPIキーを発行してください。

\`\`\`text
GET  /api/operator/v0/help/context
GET  /api/operator/v0/help/capabilities
GET  /api/operator/v0/help/safety
GET  /api/operator/v0/help/examples
GET  /api/operator/v0/dashboard/summary
GET  /api/operator/v0/account/status
GET  /api/operator/v0/settings/read
POST /api/operator/v0/templates/draft
POST /api/operator/v0/templates/preview
POST /api/operator/v0/templates/save-preview
POST /api/operator/v0/companies/search
POST /api/operator/v0/analytics/diagnose
POST /api/operator/v0/saved-lists/preview
POST /api/operator/v0/engagement/search
GET  /api/operator/v0/tracking/summary
POST /api/operator/v0/tracking/events/search
POST /api/operator/v0/tracking/ai-discovery/search
POST /api/operator/v0/tracking/visitors/search
POST /api/operator/v0/history/search
POST /api/operator/v0/deals/search
POST /api/operator/v0/deals/context
POST /api/operator/v0/deals/activities/search
POST /api/operator/v0/customers/search
POST /api/operator/v0/customers/context
\`\`\`

### Tracking / AI inflow read route

以下は通常SaaS向けの \`operator.read\` / \`operator.assist\` APIキーで利用します。ブラウザログインは不要ですが、必ず \`Authorization: Bearer <salesbreaker_api_key>\` が必要です。

\`\`\`text
GET  /api/operator/v0/tracking/summary
POST /api/operator/v0/tracking/events/search
POST /api/operator/v0/tracking/ai-discovery/search
POST /api/operator/v0/tracking/visitors/search
\`\`\`

用途は、1行タグのページ閲覧、AI referral / crawler検知、匿名訪問者の集計確認です。生IP、生User-Agent、APIトークン、CSV export、bulk report exportは扱いません。必要に応じて、SalesBreaker外のCSV、EC注文、CRMデータとAgent側で結合分析してください。

## APIキーで利用できるassist route

Directory v1では \`operator.assist.directory_v1\`、通常SaaSでは \`operator.assist\` を含むAPIキーで利用します。保存、削除、一括変更は \`confirmation_confirmed: true\` を必須にします。送信、請求、ユーザー管理は含みません。

\`\`\`text
POST /api/operator/v0/settings/company-profiles/upsert
POST /api/operator/v0/settings/company-profiles/delete
POST /api/operator/v0/settings/ng-list/upsert
POST /api/operator/v0/settings/ng-list/bulk-upsert
POST /api/operator/v0/settings/ng-list/delete
POST /api/operator/v0/settings/excluded-settings/upsert
POST /api/operator/v0/templates/save
POST /api/operator/v0/templates/delete
POST /api/operator/v0/activities/log
\`\`\`

## Assist routeの共通ルール

- \`client_id\` はAPIキーから解決します。body/queryに指定しないでください。
- 送信実行、請求、ユーザー管理はこの権限では扱いません。
- 削除、一括変更、保存は人間確認後に \`confirmation_confirmed: true\` を付けて実行してください。
- My Lists投入・更新・削除はDirectory v1では公開しません。通常SaaS向けAPIキーでも、SaaS画面と同じ課金/DNS/送信環境/attestationのunlock gateを通過しない限り実行できません。
- 送付先リストは、SalesBreaker/JAPAN GTM側で管理された \`saved_list_id\` のpreview/reviewを中心に扱います。巨大ID配列をAgent側に保持しないでください。
- 送信実行は別Gateです。送付先リストとテンプレートを確定してから、送信前確認と人間承認を通します。

## 確認Gateが必要な操作

- フォーム送信の承認付き実行。
- メール配信の承認付き実行。
- 案件・顧客情報の直接更新。
- CSVエクスポートや一括更新。
- 請求・ユーザー管理。

送信実行系は公開対象ですが、プレビューなしの直接送信はできません。送信前確認、人間の承認、重複実行防止、監査ログ、リスト再照合を通す専用導線で扱います。`,
  },
  {
    id: "limits-errors",
    title: "Limits / Errors",
    filename: "salesbreaker-limits-errors.md",
    audience: "Client",
    summary: "利用制限、よくあるエラー、復旧方法。",
    content: `# Limits / Errors

## 利用制限

初期公開では安全側に寄せた制限を置きます。

- 一覧取得は件数上限つきです。
- 送付先リストは要約、サンプル、件数、照合情報を返します。
- 巨大な対象ID配列はAPIレスポンスや利用ログに出しません。
- 2万件を超える大量処理は、画面操作または分割計画を推奨します。

## よくあるエラー

- APIキーが指定されていません。
- APIキーが停止または無効化されています。
- このAPIを使う権限がありません。
- \`operator family missing\`: APIキーに \`operator.read\` または必要なOperator系権限がありません。AIツール向け確認を含むキーを発行してください。
- \`operator_scope_required\`: APIキーに必要なOperator権限がありません。Directory-safe用途は \`operator.assist.directory_v1\`、通常SaaS用途は \`operator.assist\` を含むキーを発行してください。
- \`sales_list_quality required\`: APIキーに \`quality.read\` がありません。リスト品質確認を含むキーを発行してください。
- \`operator_confirmation_required\`: 保存、削除、一括変更には人間確認後の \`confirmation_confirmed: true\` が必要です。
- 現在のプランでは利用できません。
- URLにAPIキーが指定されています。
- 他社データへのアクセスは拒否されました。
- リスト内容が送信前確認時点から変わっています。
- 現在この操作は公開されていません。

## 復旧方法

リスト件数や内容が変わった場合は、送信前確認を作り直し、人間の確認を取り直してください。`,
  },
  {
    id: "examples",
    title: "Examples",
    filename: "salesbreaker-examples.md",
    audience: "Client / Agent",
    summary: "curl例とAIツール運用例。実行系は方針のみ。",
    content: `# Examples

## CRM送信履歴を取得する

\`\`\`bash
curl -sS "https://salesbreaker.jp/api/v1/crm/send-history?limit=20" \\
  -H "Authorization: Bearer <salesbreaker_api_key>"
\`\`\`

## リスト品質の現在状態を確認する

\`\`\`bash
curl -sS https://salesbreaker.jp/api/v1/sales-list/quality-score-runs/current \\
  -H "Authorization: Bearer <salesbreaker_api_key>"
\`\`\`

## AIツール向けの利用ガイドを取得する

この例は \`operator.read\` を含むAPIキーで実行してください。

\`\`\`bash
curl -sS https://salesbreaker.jp/api/operator/v0/help/context \\
  -H "Authorization: Bearer <salesbreaker_api_key>"
\`\`\`

## 会社概要・NG設定のread-only確認

既存の画面操作routeではなく、Operator APIのread routeを使います。

\`\`\`bash
curl -sS https://salesbreaker.jp/api/operator/v0/settings/read \\
  -H "Authorization: Bearer <salesbreaker_api_key>"
\`\`\`

## AI流入・Web計測のsummaryを確認する

この例は通常SaaS向けの \`operator.read\` または \`operator.assist\` を含むAPIキーで実行してください。Directory-safe APIキーでは利用しません。

\`\`\`bash
curl -sS "https://salesbreaker.jp/api/operator/v0/tracking/summary?days=30" \\
  -H "Authorization: Bearer <salesbreaker_api_key>"
\`\`\`

## AI discoveryログを検索する

\`\`\`bash
curl -sS https://salesbreaker.jp/api/operator/v0/tracking/ai-discovery/search \\
  -H "Authorization: Bearer <salesbreaker_api_key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "days": 30,
    "limit": 20,
    "source": "chatgpt"
  }'
\`\`\`

## 会社概要をAPIで更新する

この例は \`operator.assist\` を含むAPIキーで実行してください。\`client_id\` はAPIキーから自動判定します。

\`\`\`bash
curl -sS https://salesbreaker.jp/api/operator/v0/settings/company-profiles/upsert \\
  -H "Authorization: Bearer <salesbreaker_api_key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "confirmation_confirmed": true,
    "profile": {
      "profile_name": "Default sender profile",
      "company": "株式会社Example",
      "homepage": "https://example.com",
      "last_name": "山田",
      "first_name": "太郎",
      "email": "sender@example.com",
      "tel1": "03",
      "tel2": "1234",
      "tel3": "5678",
      "pref": "東京都",
      "address1": "千代田区..."
    }
  }'
\`\`\`

## 文面テンプレートを保存する

\`\`\`bash
curl -sS https://salesbreaker.jp/api/operator/v0/templates/save \\
  -H "Authorization: Bearer <salesbreaker_api_key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "confirmation_confirmed": true,
    "template": {
      "title": "Agent draft",
      "subject": "お問い合わせ",
      "body": "突然のご連絡失礼いたします。\\\\nhttps://example.com"
    }
  }'
\`\`\`

## NGリストを追加する

\`\`\`bash
curl -sS https://salesbreaker.jp/api/operator/v0/settings/ng-list/upsert \\
  -H "Authorization: Bearer <salesbreaker_api_key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "confirmation_confirmed": true,
    "item": {
      "company": "株式会社Example",
      "domain": "example.com",
      "reason": "User requested exclusion"
    }
  }'
\`\`\`

## 送信実行について

送信実行の単発コピー&ペースト用コマンドは、公開Docsには掲載しません。

送信実行は公開対象ですが、直接送信は不可です。Agentは送付先リスト、文面テンプレート、送信件数、送信枠、完了見込み、除外/NG/重複リスクを提示し、人間承認後に実行します。`,
  },
  {
    id: "agent-contract-pack",
    title: "JAPAN GTM Agent Contract Pack",
    filename: "salesbreaker-agent-contract-pack.md",
    audience: "Agent",
    summary: "AIがSalesBreaker各画面を扱うためのscreen別JSON契約。",
    content: `# JAPAN GTM Agent Contract Pack

Powered by SalesBreaker.

This file is the Markdown home for Agent-facing screen contracts. The \`/app/api-keys\` screen should stay lightweight; detailed JSON contracts should live in Docs / Manual.

## Purpose

\`\`\`text
Agent
↓
SalesBreaker screen
↓
browser assist / API assist
↓
preview
↓
human confirmation
↓
apply / execute only when allowed
\`\`\`

## Global Rules

\`\`\`json
{
  "workspace_freshness": {
    "check_latest_before_every_run": true,
    "version_file": "workspace-version.json",
    "latest_zip": "https://salesbreaker.jp/app/downloads/salesbreaker-agent-workspace.zip",
    "if_stale": "Stop and ask the user to download the latest JAPAN GTM Agent Workspace ZIP before operating SalesBreaker."
  },
  "auth": {
    "api_key": {
      "header": "Authorization: Bearer <salesbreaker_api_key>",
      "use_for": ["/api/v1/*", "/api/operator/v0/*"],
      "client_id_source": "resolved_from_api_key",
      "scope_required": ["crm.read", "quality.read", "operator.read.directory_v1", "operator.assist.directory_v1", "operator.read", "operator.assist", "campaign.execute"],
      "directory_safe_scope_bundles": ["operator.read.directory_v1", "operator.assist.directory_v1"],
      "normal_saas_scope_bundles": ["operator.read", "operator.assist"]
    },
    "browser_session_jwt": {
      "header": "Authorization: Bearer <login_jwt>",
      "use_for": [
        "existing browser SaaS internal routes such as /api/company-profiles",
        "/api/marketing/*",
        "/api/docs/*",
        "/api/issuer-profiles",
        "/api/client-info",
        "/api/usage-status"
      ],
      "client_id_source": "resolved_from_login_session"
    },
    "route_contract_labels": ["api_key_ready", "browser_jwt_required", "browser_assist_ok", "not_yet_api_key_enabled"],
    "prefer_operator_assist_routes_for_terminal_work": true,
    "do_not_trust": ["body.client_id", "query.client_id", "query.token"]
  },
  "operation_policy": {
    "browser_assist": "Allowed when the existing SalesBreaker screen is the safest interface.",
    "api_assist": "Use Operator route when it avoids fragile UI work.",
    "agent_role": "preparation, analysis, recording, drafting, and setup assistance",
    "operation_classes": {
      "api_read_analyze": ["settings", "history", "clicks", "tracking", "ai_inflow", "deals", "customers", "leads"],
      "api_draft_assist_write": ["company_profile", "ng_settings", "exclusion_times", "templates", "activity_logs", "setup_plans"],
      "browser_confirm_execute": ["send", "publish", "activate", "invoice", "contract", "e_sign", "payment", "dns_final_check"],
      "out_of_normal_scope": ["user_management", "permissions", "billing", "api_key_issuance", "admin_only_settings"]
    },
    "preview_required": ["save", "publish", "delete", "bulk_update", "status_change", "send_prepare"],
    "human_confirmation_required": ["save", "publish", "delete", "bulk_update", "send_execute", "billing", "user_management"],
    "send_execute": "Public capability for paid plans only; direct send is forbidden. Use saved_list/template preview, human approval, idempotency, audit, quota/speed check, then execute.",
    "mail_fallback_send": "Successful apply=true fallback emails consume email quota and write send_history; preview and queue do not consume quota.",
    "never_store": ["raw_api_key", "Authorization header", "secret", "full_large_target_id_array"]
  },
  "domain_readiness_policy": {
    "app_domain": "salesbreaker.jp is the SaaS app/login/API domain.",
    "tracking_domain": "Use tracking domain for click/open/unsubscribe URLs. Do not assume salesbreaker.jp.",
    "default_tracking_domain": "track.sbroute.net",
    "custom_tracking_domain_rule": "Use client custom tracking only when DNS, SSL, and backend route are ready.",
    "current_client_dns_records": ["DKIM CNAME x3", "tracking CNAME x1"],
    "before_send": ["company_profile", "sender_readiness", "tracking_readiness", "send_exclusion_time", "template_url_or_lp"]
  }
}
\`\`\`

## Route Auth Model

\`\`\`json
{
  "api_key_ready": "Use Authorization: Bearer <salesbreaker_api_key>. The API key must include the matching permission.",
  "browser_jwt_required": "Use the logged-in SalesBreaker browser session JWT. These routes are existing SaaS screen APIs, not SaaS API-key routes.",
  "browser_assist_ok": "The existing SalesBreaker screen can still be operated by a logged-in browser Agent.",
  "not_yet_api_key_enabled": "The screen can be handled by browser assist today, but a dedicated API-key route is not published yet.",
  "common_errors": {
    "operator family missing": "The key does not include AIツール向け確認 / operator.read, or the account is not allowed for Operator API.",
    "operator_scope_required": "The key does not include the required Operator permission. Use operator.assist.directory_v1 for Directory-safe assist or operator.assist for normal SaaS assist.",
    "operator_confirmation_required": "Save/delete/bulk/status-changing operations require confirmation_confirmed=true after human review.",
    "sales_list_quality required": "The key does not include リスト品質確認 / quality.read.",
    "invalid token": "The Authorization header is missing, malformed, disabled, revoked, or not accepted by that route."
  }
}
\`\`\`

## Operator Assist Routes

Use these with \`operator.assist.directory_v1\` for Directory-safe assist or \`operator.assist\` for normal SaaS assist. Do not include \`client_id\` in body/query.

\`\`\`json
{
  "settings_company_profile": {
    "upsert": "POST /api/operator/v0/settings/company-profiles/upsert",
    "delete": "POST /api/operator/v0/settings/company-profiles/delete",
    "confirmation_required": true
  },
  "settings_ng_list": {
    "upsert": "POST /api/operator/v0/settings/ng-list/upsert",
    "bulk_upsert": "POST /api/operator/v0/settings/ng-list/bulk-upsert",
    "delete": "POST /api/operator/v0/settings/ng-list/delete",
    "confirmation_required": true
  },
  "settings_excluded_send_time": {
    "upsert": "POST /api/operator/v0/settings/excluded-settings/upsert",
    "confirmation_required": true
  },
  "templates": {
    "save": "POST /api/operator/v0/templates/save",
    "delete": "POST /api/operator/v0/templates/delete",
    "confirmation_required": true,
    "recommendation": "Include at least one URL in outreach body when possible."
  },
  "saved_lists": {
    "preview": "POST /api/operator/v0/saved-lists/preview",
    "status": "controlled_recipient_list_review_only",
    "large_id_rule": "Do not expose huge target ID arrays in Agent notes. Use saved_list_id, count, sample, hash, and conditions.",
    "directory_not_public": ["my-lists/search", "my-lists/upsert", "my-lists/delete", "saved-lists/search", "saved-lists/save", "saved-lists/delete", "companies/search"],
    "normal_saas_conditional": ["my-lists/search", "my-lists/upsert", "my-lists/delete"],
    "normal_saas_unlock_required": ["paid_subscription_active", "trial_finished", "email_verified", "sender_profile_completed", "dkim_verified", "tracking_domain_verified", "attestation", "human_confirmation"]
  },
  "activities": {
    "log": "POST /api/operator/v0/activities/log",
    "confirmation_required": false,
    "side_effects": "Adds one activity note only; does not change deal/customer/order/NG/send state."
  }
}
\`\`\`

## Domain Readiness Contract

Use this contract when the user asks for initial setup, DNS setup, delivery troubleshooting, first-send preparation, or English/OEM domain readiness.

\`\`\`json
{
  "screen": "domain_settings.sender_and_tracking",
  "mode": "browser_assist_plus_read_context",
  "goal": "Confirm which sender and tracking domain SalesBreaker will use before messaging.",
  "rules": {
    "salesbreaker_jp": "app/login/API only; not the preferred tracking domain for generated email URLs",
    "system_tracking": "track.sbroute.net",
    "client_custom_tracking": "track.client-domain.com when dns_status=valid, ssl_status=issued, route_status=ok, active_flag=1",
    "client_dns_records": ["DKIM CNAME x3", "track CNAME x1"],
    "spf_dmarc_custom_mail_from": "not required in the current basic setup; future operational expansion"
  },
  "agent_checks": [
    "Read account/status or settings/read when available.",
    "Check the Domain Settings screen when browser assist is clearer.",
    "Do not mark DNS ready without DNS/SSL/route evidence.",
    "Ask the user which DNS/server provider they use before giving console-specific instructions.",
    "Before sending, explain whether tracking.source is system or client_custom."
  ],
  "expected_read_shape": {
    "domain_readiness": {
      "sender": {
        "sender_email": "info@example.com",
        "verified": true,
        "dkim_tokens_present": true
      },
      "tracking": {
        "current_domain": "track.example.com",
        "fallback_domain": "track.sbroute.net",
        "source": "client_custom",
        "dns_status": "valid",
        "ssl_status": "issued",
        "route_status": "ok",
        "covers": ["click", "open", "unsubscribe"]
      },
      "app": {
        "current_domain": "salesbreaker.jp",
        "source": "system"
      }
    }
  }
}
\`\`\`

## Screen Contracts

The full current screen-by-screen contracts are maintained in:

- \`docs/agent-frontend-coverage.md\`

The runtime API key page exposes a copy/downloadable \`JAPAN GTM Agent Contract Pack\` document for trusted agents. Keep this contract pack aligned with:

- \`settings.company_profile_and_ng\`
- \`templates.outreach_copy\`
- \`saved_lists.target_list_builder\`
- \`mypage.mail_fallback_after_form_send\`
- \`history_and_click_logs.agent_analysis\`
- \`mypage.results_review_manual\`
- \`deals.sfa_pipeline\`
- \`crm.customer_ledger\`
- \`marketing.lead_management\`
- \`marketing.email_campaigns\`
- \`marketing.scenario_delivery\`
- \`marketing.forms_and_lp_hosting\`
- \`marketing.presentation_builder\`

## UI Rule

Do not expand all JSON contracts on the first screen. The main API key page is for humans to understand coverage. Agents should open or download the contract pack.


---

# Full SalesBreaker Screen Contracts

# Agent Frontend Coverage

Goal:

\`\`\`text
SalesBreakerにログインしたユーザーがAgentを使って主要画面を安全に操作できる
\`\`\`

API is one implementation path. The coverage unit is the SalesBreaker feature or screen.

Important framing:

\`\`\`text
ブラウザ操作を禁止して全部API化することが目的ではない。
既存画面をAgentが操作できる領域はbrowser assistとして扱う。
画面操作routeや専用導線がある領域はAPI assistとして扱う。
保存・公開・削除・一括変更はhuman confirmationを挟む。
\`\`\`

## Route Auth Model

| Label | Meaning |
| --- | --- |
| \`api_key_ready\` | Use \`Authorization: Bearer <salesbreaker_api_key>\`. The key must include the matching permission. |
| \`browser_assist_ok\` | The logged-in SalesBreaker screen can still be operated by an Agent. Prefer Operator API for terminal-safe work when available. |
| \`not_yet_api_key_enabled\` | The screen can be handled by browser assist today, but a dedicated API-key route is not published yet. |

Common API-key errors:

| Error | Meaning |
| --- | --- |
| \`operator family missing\` | The key does not include \`operator.read\` / AIツール向け確認, or the account is not allowed for Operator API. |
| \`sales_list_quality required\` | The key does not include \`quality.read\` / リスト品質確認. |
| \`operator_scope_required\` | The API key is valid, but it lacks the required Operator permission. Use \`operator.assist.directory_v1\` for Directory-safe assist or \`operator.assist\` for normal SaaS assist. |
| \`operator_confirmation_required\` | Save/delete/bulk/status-changing operations need \`confirmation_confirmed: true\` after human review. |

## Status Labels

| Status | Meaning |
| --- | --- |
| browser assistでOK | Agent can operate or inspect the existing SalesBreaker screen. |
| API assistあり | Internal API or Operator route can help the Agent avoid fragile browser-only workflows. |
| preview推奨 | Agent should draft, summarize, or show a diff before any real change. |
| confirmation必須 | Save, publish, delete, status change, bulk update, or account-sensitive action needs human approval. |
| 有料Gate | Send execution, real enqueue, target_set, bulk send gates, or public developer exposure. |

## Feature Coverage

| Feature | Current Agent State | Agent Use |
| --- | --- | --- |
| 会社概要＆NG設定 | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | 会社概要、送信者情報、NGリスト、送信除外時間を確認・整形。NG追加や設定保存は確認付き。 |
| 文面テンプレート | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | 下書き、改善提案、URL不足チェック、保存前レビュー。保存・削除は確認付き。 |
| マイリスト管理 | Directoryでは非公開 / 通常SaaSでは条件付き | Directory-safe API・Agent Workspaceでは任意リスト投入/更新/削除を公開しない。通常SaaS APIでは課金/DNS/送信環境/attestation/human confirmationのunlock gateを通過したアカウントだけが使える。 |
| 送付先リスト | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | 件数、品質、除外理由、サンプル確認。巨大ID配列をAgentに持たせずリスト単位で扱う。 |
| 送信履歴 | browser assistでOK / API assistあり | 送信結果、失敗理由、反応状況を読み取り、改善やフォロー候補を整理。 |
| 案件管理 | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | 見込み管理、商談後メモ、架電/アプローチ記録、次アクション、見積書作成支援。ステータス更新・受注化・NG追加・見積発行は確認付き。 |
| 顧客台帳CRM | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | 受注後の顧客情報、担当者、契約、請求/見積/領収、契約書、電子契約、対応履歴を整理。作成・更新・送信・決済連携は確認付き。 |
| クリックログ | browser assistでOK / API assistあり | クリック・反応ログからホットリードやフォロー優先度を抽出。 |
| リード管理 | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | アクティブリード、ハウスリード、匿名ログを整理し、案件化候補とメルマガ/シナリオの宛先設計を支援。状態変更、案件化、CSV取込、配信対象反映は確認付き。 |
| メール配信 | browser assistでOK / API assistあり / preview推奨 / confirmation必須 / 有料Gate | AgentがHTMLメルマガ本文、件名、CTA、宛先条件、テスト送信計画を作成。下書き保存・更新は確認付き、即時/予約配信は有料プラン + 確認付き実行Gate。 |
| シナリオ配信 | browser assistでOK / API assistあり / preview推奨 / confirmation必須 / 有料Gate | Agentがフォーム/タグ/手動トリガー、待機時間、メールステップ、開封/クリック条件を整理。保存・有効化・削除は確認付き、送信自動化は有料Gate。 |
| フォーム作成 | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | Agentがフォーム項目、タグ、通知、自動返信、完了後導線を設計。LPと連動するため、作成/更新/削除/外部タグ反映は確認付き。 |
| LP制作・公開 | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | AgentがHTML一体型LPを作成し、フォーム連携、画像、公開URL、OGP、公開状態を確認。CSS/JSは別欄に分けずHTML内へ集約。 |
| 資料作成 | browser assistでOK / API assistあり / preview推奨 / confirmation必須 | Agentが資料構成を作り、1ページごとにHTMLスライドを生成。人間は順番、文言、グラフ、画像、細部を後から編集。削除・並び替え・PDF出力は確認付き。 |
| マーケティング設定 | browser assistでOK / preview推奨 / confirmation必須 | DNS設定、タグ、CAPI、計測設定、連携状態を確認。設定変更は確認付き。 |
| アカウント管理 | browser assistでOK / API assistあり | プラン、利用状況、送信準備状態を確認。請求や契約変更は対象外。 |
| ユーザー管理 | browser assistでOK / confirmation必須 | ユーザー一覧や権限状態の確認。招待、停止、権限変更は当面ブラウザ確認前提。 |

## 有料Gate / 後回し

| Feature | Reason |
| --- | --- |
| 送信実行 | 別扱い。送信前確認と承認付き実行 |
| saved_list_id real enqueue | 有料Gate |
| target_set / 大量送信 | 有料Gate |
| 配信基盤接続 | 有料Gate |
| public MCP / OpenAPI | 有料Gate developer product |

## Release Note

The UI should communicate:

\`\`\`text
送信実行は別扱いです。
送信実行は有料プラン、送信枠、送信前確認、人間承認、監査ログを通して扱います。直接送信は不可です。
\`\`\`

## Agent Contract: settings.company_profile_and_ng

This page should be treated as API assist first. This section is primarily for Codex / Claude Code, not for human explanation copy.

Agent flow:

\`\`\`text
ユーザーが会社概要やNG条件を雑に伝える
↓
Agentが既存設定をAPIで読む
↓
Agentが不足項目を質問
↓
Agentが会社概要/NGリストを構造化
↓
previewで差分・警告・確認事項を返す
↓
人間が確認
↓
API apply
↓
画面で最終確認
\`\`\`

Contract:

\`\`\`json
{
  "screen": "settings.company_profile_and_ng",
  "mode": "api_key_assist_first_plus_browser_confirm",
  "route_auth": {
    "api_key_status": "api_key_ready_for_settings_read_and_assist_write",
    "browser_session_status": "browser_assist_ok",
    "api_key_read": "GET /api/operator/v0/settings/read",
    "api_key_write_scope": "operator.assist",
    "client_id_source": "resolved_from_api_key"
  },
  "read": [
    {
      "route": "GET /api/operator/v0/settings/read",
      "auth": "api_key_ready",
      "scope": "operator.read"
    },
    {
      "route": "GET /api/company-profiles",
      "auth": "browser_assist_ok"
    },
    {
      "route": "GET /api/ng-list",
      "auth": "browser_assist_ok"
    },
    {
      "route": "GET /api/excluded-settings",
      "auth": "browser_assist_ok"
    },
    {
      "route": "GET /api/users",
      "auth": "browser_assist_ok"
    }
  ],
  "preview_required": true,
  "confirmation_required": [
    "company_profile.create",
    "company_profile.update",
    "company_profile.delete",
    "ng_list.create",
    "ng_list.update",
    "ng_list.delete",
    "ng_list.bulk_import",
    "ng_list.bulk_delete",
    "excluded_settings.update"
  ],
  "apply": {
    "company_profile": {
      "method": "POST or PUT",
      "route": "POST /api/operator/v0/settings/company-profiles/upsert",
      "auth": "api_key_ready",
      "scope": "operator.assist",
      "confirmation_confirmed": true,
      "payload": {
        "confirmation_confirmed": true,
        "profile": {
        "profile_name": "営業用 標準プロフィール",
        "staff_email": "sender@example.com",
        "company": "株式会社Example",
        "department": "営業部",
        "position": "代表取締役",
        "employee_count": "10名",
        "homepage": "https://example.com",
        "last_name": "山田",
        "first_name": "太郎",
        "last_name_kana": "ヤマダ",
        "first_name_kana": "タロウ",
        "email": "sender@example.com",
        "tel1": "03",
        "tel2": "1234",
        "tel3": "5678",
        "zip1": "100",
        "zip2": "0001",
        "pref": "東京都",
        "address1": "千代田区...",
        "address2": "Exampleビル"
        }
      }
    },
    "ng_list": {
      "method": "POST or PUT",
      "route": "POST /api/operator/v0/settings/ng-list/upsert",
      "auth": "api_key_ready",
      "scope": "operator.assist",
      "confirmation_confirmed": true,
      "payload": {
        "confirmation_confirmed": true,
        "item": {
          "company": "株式会社Example",
          "domain": "example.com",
          "url_pattern": "https://example.com/contact",
          "reason": "既存取引先のため除外"
        }
      }
    },
    "excluded_settings": {
      "method": "POST",
      "route": "POST /api/operator/v0/settings/excluded-settings/upsert",
      "auth": "api_key_ready",
      "scope": "operator.assist",
      "confirmation_confirmed": true,
      "payload": {
        "confirmation_confirmed": true,
        "excluded_settings": {
          "excluded_days": ["土", "日"],
          "excluded_hours_from": "22:00",
          "excluded_hours_to": "08:00"
        }
      }
    }
  }
}
\`\`\`

Rules:

- Use the contract shape above when preparing API calls.
- \`domain\` is lower-case, no protocol, no path.
- URL input should populate both \`domain\` and \`url_pattern\` when appropriate.
- company-only input is allowed but should be previewed.
- missing reason should become a clear default such as \`ユーザー指定\`.
- duplicate, ambiguous, or risky rows go to \`needs_confirmation\`.
- Agent must not invent unknown company/profile fields.
- Saving profile, deleting profile, adding/updating/deleting NG rows, bulk import/delete, and exclusion time changes require human confirmation.

## Agent Contract: templates.outreach_copy

This page is primarily an Agent-assisted copywriting surface. The client and Agent discuss the angle, offer, URL, tone, and target audience, then the Agent drafts the template and returns a preview before saving.

Agent flow:

\`\`\`text
ユーザーが送信したい内容や商材を伝える
↓
Agentが既存テンプレートと会社概要を読む
↓
Agentが件名/本文を作成し、URL不足や弱い訴求を警告する
↓
previewで本文・件名・差し込み結果を返す
↓
人間が確認
↓
API apply or browser save
\`\`\`

Contract:

\`\`\`json
{
  "screen": "templates.outreach_copy",
  "mode": "agent_draft_and_preview",
  "route_auth": {
    "existing_browser_routes": "browser_assist_ok",
    "operator_preview_routes": "api_key_ready_when_operator.read_or_campaign.execute_scope_exists",
    "operator_save_routes": "api_key_ready_with_operator.assist"
  },
  "human_intent": "Client and Agent collaborate to create outbound copy.",
  "read": [
    {
      "route": "GET /api/templates",
      "auth": "browser_assist_ok"
    },
    {
      "route": "GET /api/company-profiles",
      "auth": "browser_assist_ok"
    },
    {
      "route": "GET /api/load",
      "auth": "browser_assist_ok"
    }
  ],
  "operator_assist": [
    {
      "route": "POST /api/operator/v0/templates/draft",
      "auth": "api_key_ready"
    },
    {
      "route": "POST /api/operator/v0/templates/preview",
      "auth": "api_key_ready"
    },
    {
      "route": "POST /api/operator/v0/templates/save-preview",
      "auth": "api_key_ready"
    },
    {
      "route": "POST /api/operator/v0/templates/save",
      "auth": "api_key_ready",
      "scope": "operator.assist"
    },
    {
      "route": "POST /api/operator/v0/templates/delete",
      "auth": "api_key_ready",
      "scope": "operator.assist"
    }
  ],
  "preview_required": true,
  "confirmation_required": [
    "template.create",
    "template.update",
    "template.delete",
    "template.duplicate"
  ],
  "apply": {
    "template": {
      "method": "POST or PUT",
      "route": "POST /api/operator/v0/templates/save",
      "auth": "api_key_ready",
      "scope": "operator.assist",
      "payload": {
        "confirmation_confirmed": true,
        "template": {
        "title": "漫画採用 初回接触",
        "company_profile_id": 123,
        "subject": "採用広報についてのご相談",
        "body": "株式会社{{会社名}}\\nご担当者様\\n\\n突然のご連絡失礼いたします。...\\n\\n詳細はこちらをご確認ください。\\nhttps://example.com/campaign",
        "allow_resend": false
        }
      }
    }
  },
  "agent_output": {
    "draft": {
      "title": "string",
      "subject": "Japanese subject line",
      "body": "Japanese outreach body with a concrete URL when possible"
    },
    "warnings": [
      "missing_url",
      "too_generic",
      "unclear_offer",
      "missing_company_profile"
    ],
    "needs_confirmation": []
  },
  "token_policy": {
    "recommended_tokens": ["{{会社名}}"],
    "rule": "Do not use unlabeled shared params. Prefer concrete copy and a concrete URL."
  }
}
\`\`\`

Rules:

- The Agent should draft Japanese subject/body copy unless the user explicitly asks otherwise.
- A concrete URL should be included when a campaign page, LP, article, service page, or material URL is known.
- \`{{会社名}}\` is the only recommended token for new Agent-created copy.
- Do not use unlabeled shared params for new copy.
- Saving, updating, duplicating, or deleting templates requires human confirmation.
- Template preview must not create tracking links, unsubscribe tokens, send history, or send jobs.

## Directory Closed Surface: arbitrary_house_list_import

This arbitrary house-list import/update/delete workflow is not exposed in the Directory-safe Codex-facing Agent Workspace. Normal SaaS API access is conditional and must pass the same unlock gate and attestation gate as the SalesBreaker screen. Use \`saved_lists.target_list_builder\` and \`POST /api/operator/v0/saved-lists/preview\` for controlled SalesBreaker/JAPAN GTM recipient-list review.

## Agent Contract: saved_lists.target_list_builder

This page is the send-target saved-list builder. The Agent should use scored / cleansed SalesBreaker candidates, apply the user's desired audience conditions, return a count and sample first, then save the target list only after human confirmation.

Agent flow:

\`\`\`text
ユーザーが狙いたい業種・地域・条件を伝える
↓
Agentが条件を構造化
↓
count previewで件数を返す
↓
sample previewで対象例・品質・除外理由を返す
↓
人間がOK
↓
20,000件以内なら保存
↓
20,000件超なら 001 / 002 のように分割保存計画を返す
\`\`\`

Contract:

\`\`\`json
{
  "screen": "saved_lists.target_list_builder",
  "mode": "count_first_then_save",
  "route_auth": {
    "existing_saved_list_routes": "browser_assist_ok",
    "sales_list_quality_routes": "api_key_ready_with_quality.read",
    "operator_preview_routes": "api_key_ready_with_operator.read",
    "operator_save_routes": "not_public_in_codex_workspace"
  },
  "human_intent": "Agent builds a send-target saved list from scored / cleansed SalesBreaker candidates based on the user's desired audience.",
  "read": [
    {
      "route": "GET /api/saved-lists",
      "auth": "browser_assist_ok"
    },
    {
      "route": "GET /api/saved-lists/:id",
      "auth": "browser_assist_ok"
    },
    {
      "route": "POST /api/load/count",
      "auth": "browser_assist_ok"
    },
    {
      "route": "POST /api/load",
      "auth": "browser_assist_ok"
    },
    {
      "route": "GET /api/v1/sales-list/quality-scores",
      "auth": "api_key_ready"
    },
    {
      "route": "POST /api/operator/v0/saved-lists/preview",
      "auth": "api_key_ready"
    }
  ],
  "apply": {
    "status": "browser_assist_or_future_controlled_target_set_only",
    "api_key_routes": "not_public_in_codex_workspace",
    "note": "Codex-facing API keys can preview/review existing controlled recipient lists. Creating/updating/deleting recipient lists remains browser-assisted or Phase2 controlled target-set work."
  },
  "query_preview": {
    "method": "POST",
    "route": "/api/load/count",
    "payload": {
      "listSourceType": "master",
      "filters": {
        "industry_class": ["IT・Web"],
        "address": { "東京都": ["渋谷区", "新宿区"] },
        "business_tags": ["採用", "広報"],
        "list_tags": [],
        "searchTerm": "",
        "sentStatus": "unsent",
        "globalSendStatus": "all",
        "globalClickStatus": "all",
        "minGlobalSends": ""
      }
    },
    "response_expected": {
      "totalItems": 1240
    }
  },
  "sample_preview": {
    "method": "POST",
    "route": "/api/load",
    "payload": {
      "page": 1,
      "limit": 20,
      "listSourceType": "master",
      "filters": "same as query_preview.filters"
    },
    "return": [
      "company",
      "url",
      "contact_url",
      "industry_class",
      "business_tags",
      "list_tags",
      "score",
      "grade",
      "recommended_next_action",
      "my_latest_status",
      "global history summary"
    ]
  },
  "save_policy": {
    "count_first": true,
    "human_confirmation_required": true,
    "single_list_limit": 20000,
    "if_count_exceeds_limit": "split",
    "split_naming": "{base_name}_001, {base_name}_002, ...",
    "do_not_execute_send": true
  },
  "blocked": [
    "campaign.execute",
    "send_execution.enqueue",
    "csv_export_by_agent",
    "save_without_count_preview",
    "save_over_20000_without_split"
  ]
}
\`\`\`

Rules:

- Count first. Do not save a send-target list before returning the count.
- Return sample targets and quality/exclusion summary before save.
- Human confirmation is required before create, update, or delete.
- The Agent should prefer scored / cleansed candidates and keep the filter JSON in the list description or operator evidence.
- The UI limit is 20,000 targets per list for Agent handling. Larger target sets should be split with suffixes such as \`_001\`, \`_002\`.
- Creating the list does not mean sending. \`campaign.execute\` stays in the separate send approval flow.

## Agent Contract: mypage.mail_fallback_after_form_send

MyPage includes a post-job recovery path for targets where form submission did not reach the company, but the report contains public email fallback evidence. Treat this as a send-adjacent capability: the Agent may inspect, prepare, and preview candidates, but must not auto-send without explicit human confirmation.

Contract:

\`\`\`json
{
  "screen": "mypage.mail_fallback_after_form_send",
  "mode": "post_form_send_public_email_fallback",
  "human_page": "/mypage",
  "target_statuses": ["NO_FORM_FOUND", "DEAD_URL"],
  "requires_verified_sender": true,
  "verified_sender_source": "issuer_profiles.sender_email with ses_verification_status in Verified/Success",
  "routes": {
    "summary": "GET /api/mail-fallback/jobs/{job_id}/summary",
    "prepare": "POST /api/mail-fallback/jobs/{job_id}/prepare",
    "queue": "POST /api/mail-fallback/jobs/{job_id}/queue",
    "preview_or_send": "POST /api/mail-fallback/send"
  },
  "preview_body": {
    "client_id": "client001",
    "job_id": 5573,
    "template_variant": "public_email_fallback",
    "limit": 3,
    "apply": false
  },
  "send_body": {
    "client_id": "client001",
    "job_id": 5573,
    "template_variant": "public_email_fallback",
    "limit": 1000,
    "apply": true
  },
  "write_boundary": {
    "prepare_apply_true": ["public_contact_email_inventory upsert"],
    "queue_apply_true": ["mail_fallback_events insert queued"],
    "send_apply_true": [
      "mail_fallback_events status updates",
      "monthly_usage_limits.current_email_sends increments for successful sends",
      "send_history row insert for successful fallback emails",
      "unsubscribe_tokens insert",
      "client_email_click_logs tracking inserts",
      "actual email send"
    ]
  },
  "usage_and_history_policy": {
    "prepare": "does_not_consume_email_quota",
    "queue": "does_not_consume_email_quota",
    "preview": "does_not_consume_email_quota",
    "send_success": "consumes_email_quota_one_per_successful_email_and_writes_send_history",
    "send_failure": "does_not_consume_email_quota; remains visible in mail_fallback_events",
    "send_history_marker": "send_history.error_details includes [mail_fallback]"
  },
  "agent_policy": {
    "summary": "safe read",
    "prepare": "confirmation recommended",
    "preview": "allowed before human approval",
    "queue": "human confirmation required",
    "send": "human confirmation required; separate send gate; successful sends consume email quota"
  }
}
\`\`\`

Rules:

- Do not automatically send public-email fallback after a form-send job.
- Do not offer fallback send when no verified sender profile exists.
- Only target failed form outcomes such as \`NO_FORM_FOUND\` and \`DEAD_URL\`.
- Show candidate count, sender email, subject/body preview, unsubscribe/tracking behavior, and one-job-once policy before approval.
- This is useful, but it is still email sending. Keep it separate from ordinary read/analysis operations.

## Agent Contract: history_and_click_logs.agent_analysis

Send history and click logs remain useful human review screens, but the Agent should not depend on visually scraping those pages. For Agent work, treat them as read-only signal sources and aggregate them through bounded history / engagement / analytics routes.

Agent flow:

\`\`\`text
ユーザーが「反応見て」「次どこ追う？」と依頼
↓
Agentが期間・テンプレ・JOB・対象条件を決める
↓
history.readで送信結果を集計
↓
engagement.readでクリック/Web/メール反応を集計
↓
analytics.diagnoseで改善候補・フォロー候補を返す
↓
必要なら案件化/タスク化は別confirmation
\`\`\`

Contract:

\`\`\`json
{
  "screen": "history_and_click_logs.agent_analysis",
  "mode": "agent_aggregate_read_only",
  "human_intent": "Human pages stay useful for visual review; Agent should aggregate send history and engagement signals through read-only logic.",
  "human_pages": [
    "/history",
    "/click-logs"
  ],
  "agent_read": [
    "POST /api/operator/v0/history/search",
    "POST /api/operator/v0/engagement/search",
    "POST /api/operator/v0/analytics/diagnose"
  ],
  "source_tables": [
    "send_history",
    "click_logs",
    "web_tracking_logs",
    "client_email_click_logs",
    "visitor_intelligence",
    "deals"
  ],
  "request": {
    "period": { "days": 30, "max_days": 90 },
    "pagination": { "limit": 100, "max_limit": 100 },
    "filters": {
      "status": ["success", "needs_review", "fail", "no_form_found", "dead_url", "wrong_form_type", "sales_rejected"],
      "template_id": "optional",
      "job_id": "optional",
      "target_key": "master:{id} or client:{id}",
      "company_query": "optional"
    }
  },
  "agent_output": {
    "summary": {
      "sent_count": 0,
      "success_count": 0,
      "needs_review_count": 0,
      "fail_count": 0,
      "click_count": 0,
      "hot_lead_count": 0
    },
    "breakdowns": {
      "by_status": [],
      "by_template": [],
      "by_failure_reason": [],
      "by_engagement_type": []
    },
    "hot_leads": [
      {
        "target_key": "master:123",
        "company": "株式会社Example",
        "signals": ["clicked", "web_visit", "recent_success"],
        "recommended_next_action": "create_deal_or_follow_up_task"
      }
    ],
    "next_actions": [
      "follow_up_hot_leads",
      "review_failed_contact_urls",
      "improve_template_copy",
      "create_saved_list_from_positive_signals"
    ]
  },
  "blocked": [
    "csv_export_by_agent",
    "job_creation",
    "send_execution",
    "tracking_link_creation",
    "unsubscribe_token_creation",
    "db_write"
  ]
}
\`\`\`

Rules:

- Human pages are for visual review. Agent analysis should use read-only aggregation routes.
- \`history.read\` is for bounded \`send_history\` reads.
- \`engagement.read\` is for click / web / email engagement signals.
- \`analytics.diagnose\` is for summarized recommendations.
- CSV export is not part of the Agent default flow.
- Follow-up writes such as deal creation, task creation, or status changes require separate confirmation.

## Agent Contract: mypage.results_review_manual

MyPage is not only a job monitor. It is also the first place where humans ask, "How did the send go?" The Agent should turn the visible numbers into an operating judgment: delivery quality, target/list quality, engagement timing, and next action.

Contract:

\`\`\`json
{
  "screen": "mypage.results_review_manual",
  "mode": "agent_result_interpretation",
  "human_page": "/mypage",
  "browser_assist": true,
  "terminal_api_assist": true,
  "read_sources": [
    "POST /api/operator/v0/history/search",
    "POST /api/operator/v0/engagement/search",
    "POST /api/operator/v0/analytics/diagnose",
    "GET /api/v1/crm/click-events",
    "GET /api/send-job-summaries?client_id={client_id}&limit=10",
    "GET /api/mail-fallback/jobs/{job_id}/summary"
  ],
  "human_export_fallback": [
    "GET /api/send-job-summaries/{job_id}/report.xlsx?client_id={client_id}"
  ],
  "source_policy": "Agents must read results through the operator/crm APIs above. The xlsx report is a human-facing export: fetch it only when the human explicitly asks for the Excel file itself, never as an agent data source.",
  "status_interpretation": {
    "SUCCESS": "Reached the intended delivery path. This is not the same as reply, appointment, or deal.",
    "NEEDS_REVIEW": "Potentially useful but ambiguous. Inspect sample rows before deciding.",
    "NO_FORM_FOUND": "Contact-url/form discovery issue. Treat as list/contact-url quality, not copy quality.",
    "DEAD_URL": "URL quality issue. Clean list before judging campaign or template.",
    "WRONG_FORM_TYPE": "Form exists but is not appropriate. Improve contact URL/source quality.",
    "SALES_REJECTED": "Site rejected sales contact. Feed exclusion/NG learning when confirmed.",
    "FAIL": "Technical or unknown failure. Inspect samples/logs.",
    "CLICK_2_PLUS": "Hot lead candidate. Recommend deal/task/follow-up consideration."
  },
  "analysis_windows": {
    "right_after_completion": [
      "delivery_result_breakdown",
      "failed_url_quality",
      "mail_fallback_candidates",
      "obvious_hot_clicks"
    ],
    "after_48_hours": [
      "click_rate",
      "hot_leads",
      "template_vs_list_quality",
      "next_follow_up_actions"
    ],
    "after_7_days": [
      "template_comparison",
      "saved_list_comparison",
      "longer_tail_engagement"
    ]
  },
  "agent_output": {
    "plain_language_summary": "string",
    "numbers": {
      "target_count": 0,
      "success_count": 0,
      "needs_review_count": 0,
      "unreachable_count": 0,
      "technical_or_policy_fail_count": 0,
      "click_count": 0,
      "hot_lead_count": 0
    },
    "what_looks_good": [],
    "what_needs_review": [],
    "what_not_to_conclude_yet": [],
    "recommended_next_actions": [
      "review_needs_review_samples",
      "clean_dead_or_missing_contact_urls",
      "create_deal_or_task_for_hot_leads",
      "wait_48_hours_then_reanalyze",
      "draft_template_revision"
    ],
    "suggested_message_to_user": "string"
  },
  "performance_benchmarks": {
    "primary_denominator": "successful_form_or_email_delivery_count",
    "very_good_case": "about 1 appointment per 1,000 successful deliveries",
    "normal_case": "about 1 appointment per 10,000 successful deliveries can be normal for broad cold outreach",
    "never_guarantee": true,
    "depends_on": [
      "offer",
      "copy",
      "target segment",
      "contact_url quality",
      "LP/material quality",
      "timing",
      "brand/trust"
    ]
  },
  "copy_and_lp_rules": {
    "form_copy_default_length": "250-450 Japanese characters",
    "form_copy_soft_max": "600 Japanese characters unless the form clearly allows more",
    "form_copy_structure": [
      "short greeting",
      "why this company is relevant",
      "one concrete offer/benefit",
      "one CTA URL",
      "short signature"
    ],
    "avoid": [
      "long company introduction",
      "multiple URLs",
      "emoji-heavy copy",
      "HTML",
      "excessive punctuation",
      "abstract buzzwords"
    ],
    "insert_lp_when": [
      "offer needs more explanation than a form allows",
      "clicks exist but appointments do not happen",
      "successful sends are high but response is weak",
      "the product needs proof, visuals, pricing, or case examples"
    ],
    "lp_role": "Use the form as a short hook and the LP/material as the detailed explanation."
  },
  "operational_readiness_before_blame": {
    "company_profile_required_fields": [
      "company",
      "staff_email",
      "last_name",
      "first_name",
      "tel1/tel2/tel3",
      "zip1/zip2",
      "pref/address1",
      "homepage"
    ],
    "sender_readiness": [
      "sender email exists",
      "DNS/SES verification complete when email delivery is involved"
    ],
    "common_failure_causes": {
      "phone_missing": "End forms often require phone. Empty company profile phone can block sending.",
      "address_missing": "Some forms require postal code/address.",
      "url_missing": "No URL means no clear next step for the prospect.",
      "too_little_volume": "Do not judge appointment performance from tiny volume.",
      "clicks_without_followup": "Multiple clicks should trigger phone/email/deal/task follow-up.",
      "calendar_link_noise": "If direct scheduling link causes bad bookings, place an LP/filtering page before scheduling."
    },
    "agent_required_diagnosis_order": [
      "company_profile_completeness",
      "sender_dns_readiness",
      "template_url_and_length",
      "target/contact_url_quality",
      "sent_success_volume",
      "click/followup_operations"
    ]
  },
  "sample_human_message": "今回の結果は、文面評価とリスト/URL品質を分けて見るのがよさそうです。SUCCESSは到達できた件数で、返信や商談化ではありません。NO_FORM_FOUND/DEAD_URL/WRONG_FORM_TYPEは文面よりリスト品質の問題として扱います。クリックは送信直後には増えにくいので、48時間後にホットリードとテンプレ別の反応を再分析しましょう。",
  "sample_benchmark_message": "商材・ターゲット・文面でかなり変わりますが、フォーム営業では成功送信1,000件で1アポならかなり良い、1万件で1アポでも普通にあり得ます。アポ数だけで即判断せず、フォーム到達率、クリック/LP遷移、ターゲットと訴求のズレ、文面の短さ、LP/資料への逃がし方を分けて見ましょう。",
  "sample_operational_gap_message": "SalesBreaker側の問題と判断する前に、会社概要・送信者設定・文面URL・送付先URL品質・送信量・クリック後フォローを確認しましょう。送信先フォームが電話番号必須なのに会社概要の電話番号が未入力なら、送信できないのは自然です。クリックが複数ある会社は放置せず、電話・個別メール・案件化・タスク化に進めるべきです。",
  "do_not_overclaim": [
    "do not call a campaign successful only because SUCCESS rows exist",
    "do not call a template bad because NO_FORM_FOUND or DEAD_URL is high",
    "do not judge click performance too early",
    "do not merge fallback email results with original form-send results without labeling them",
    "do not create deals/tasks/NG rows from analysis without user intent or confirmation",
    "do not promise appointments from a fixed number of sends",
    "do not write long form copy when a short hook plus LP/material URL is more appropriate",
    "do not blame SalesBreaker before checking company profile completeness and follow-up operations"
  ],
  "revocomi_validation_policy": {
    "client_id": "client001",
    "company_name": "株式会社RevoComi",
    "purpose": "pre-release validation account",
    "allowed_when_owner_says": true,
    "test_marker_required": true,
    "send_execution_requires_explicit_instruction": true,
    "record": [
      "route_or_screen",
      "request_shape",
      "created_ids",
      "before_after_state",
      "browser_refresh_result",
      "cleanup_result_or_reason_not_cleaned"
    ]
  }
}
\`\`\`

Rules:

- Results should be translated into business meaning, not simply echoed.
- Separate target/list/contact-url quality from template/copy quality.
- Recommend a delayed engagement review when clicks are too fresh.
- RevoComi can be used for pre-release validation when explicitly allowed, but send execution remains explicit-approval only.

## Agent Contract: deals.sfa_pipeline

DealsPage is the simple SFA / opportunity management surface. The Agent should help the user after outreach, calls, and meetings: record what happened, summarize the deal context, propose next actions, create follow-up tasks, and prepare quotations. The in-page AI sales consultation is treated as legacy because Codex / Claude Code should now provide that advisory layer outside the old product-management flow.

Agent flow:

\`\`\`text
ユーザーが「この会社にアプローチした」「電話した」「商談した」と伝える
↓
Agentがdeal/context/history/engagementを読む
↓
Agentが商談メモ、ステータス候補、次アクション、タスク、見積案を構造化
↓
previewで更新内容・見積内容・副作用候補を返す
↓
人間が確認
↓
activity.log / task.create / browser save / quotation.generate
\`\`\`

Contract:

\`\`\`json
{
  "screen": "deals.sfa_pipeline",
  "mode": "agent_followup_and_quote_assist",
  "human_intent": "DealsPage is a simple SFA pipeline. Agent helps after outreach, calls, and meetings by logging context, proposing next actions, and preparing quotation drafts.",
  "human_page": "/deals",
  "read": [
    "GET /api/deals",
    "GET /api/deals/details/:deal_id",
    "GET /api/deals/:deal_id/quotations",
    "GET /api/deals/:salesListId/web-timeline",
    "POST /api/operator/v0/deals/search",
    "POST /api/operator/v0/deals/context",
    "POST /api/operator/v0/deals/activities/search",
    "POST /api/operator/v0/history/search",
    "POST /api/operator/v0/engagement/search"
  ],
  "safe_write": [
    "POST /api/operator/v0/activities/log",
    "POST /api/operator/v0/tasks",
    "POST /api/operator/v0/tasks/search",
    "POST /api/operator/v0/tasks/:task_id/complete"
  ],
  "preview_or_browser_apply": {
    "deal_update": {
      "route": "/api/deals/save",
      "payload": {
        "sales_list_id": 123,
        "list_type": "master or client",
        "status": "商談",
        "angle": "A",
        "call_status": "アポイント",
        "comment": "商談後メモ。課題、温度感、決裁者、次回提案内容を記録。",
        "sales_staff_email": "owner@example.com",
        "cs_staff_email": "cs@example.com",
        "next_action_date": "2026-06-10",
        "next_action_note": "見積書を作成してURL共有"
      }
    },
    "quotation_generate": {
      "route": "/api/deals/quotations/generate",
      "requires": [
        "existing deal_id",
        "confirmed customer_name",
        "confirmed issuer",
        "confirmed line items",
        "confirmed tax/total",
        "human confirmation"
      ],
      "returns": {
        "quotation_url": "/app/quotation/{viewToken}"
      }
    }
  },
  "agent_output": {
    "deal_summary": {
      "current_status": "未設定 | アポ | 商談 | 失注 | クレーム | 受注",
      "engagement_signals": ["clicked", "web_visit", "successful_send"],
      "latest_activity": "string"
    },
    "meeting_note": {
      "pain_points": [],
      "decision_maker": "string or unknown",
      "budget_timing": "string or unknown",
      "objections": [],
      "next_action": "string"
    },
    "recommended_update": {
      "status": "string",
      "angle": "S|A|B|C|D|E",
      "call_status": "string",
      "next_action_date": "YYYY-MM-DD",
      "next_action_note": "string"
    },
    "quote_draft": {
      "customer_name": "string",
      "items": [
        { "name": "string", "quantity": 1, "unit_price": 0 }
      ],
      "notes": "string"
    },
    "warnings": [],
    "needs_confirmation": []
  },
  "confirmation_required": [
    "deal.status.update",
    "deal.call_status.update",
    "deal.owner.update",
    "deal.next_action.update",
    "quotation.generate",
    "order.convert",
    "customer.update",
    "ng_list.add",
    "deal.delete"
  ],
  "legacy_or_blocked": [
    "ai_sales_consultation_legacy",
    "automatic_order_conversion",
    "automatic_ng_add",
    "quotation_generate_without_confirmed_items",
    "bulk_deal_update_without_preview",
    "campaign.execute"
  ]
}
\`\`\`

Rules:

- Treat the old in-page AI sales consultation as legacy. The external Agent should provide the sales advice.
- Use \`activity.log\` for approach, call, and meeting notes when the user only wants to record context.
- Use \`task.create\` for future follow-up work instead of burying future tasks only in a comment.
- Use deal/context/history/engagement reads before suggesting status, priority, next action, or quotation work.
- Status update, call status update, owner change, next action update, quotation generation, order conversion, customer update, NG add, and delete require human confirmation.
- Quotation generation should happen only after the deal exists and the human confirms recipient, issuer, line items, totals, and notes.

## Agent Contract: crm.customer_ledger

CustomerLedgerPage is the post-order CRM surface. It is bigger than a normal customer list: it includes customer profile, contacts, contracts, contract documents, electronic signature, invoices/quotations/receipts, subscriptions/payment links, customer timeline, issuer profiles, billing product master, contract templates, and CSV import.

Agent flow:

\`\`\`text
ユーザーが「この顧客どうなってる？」「契約/請求/次フォロー整理して」と依頼
↓
Agentがcustomer.read/contextで顧客・担当者・契約・請求・履歴を読む
↓
Agentが状況要約、リスク、次アクション、下書きを返す
↓
必要ならtimeline note / taskを下書き
↓
契約・請求・電子契約・決済・一括更新は人間確認
↓
browser apply or API apply
\`\`\`

Contract:

\`\`\`json
{
  "screen": "crm.customer_ledger",
  "mode": "agent_customer_success_and_backoffice_assist",
  "human_intent": "CustomerLedgerPage manages post-order customers. Agent helps read customer context, organize contacts/contracts/billing/timeline, draft documents, and propose next actions.",
  "human_page": "/customer-ledger",
  "tabs": [
    "基本情報",
    "契約管理",
    "契約書発行",
    "電子署名",
    "担当者",
    "販売管理",
    "対応履歴"
  ],
  "read": [
    "GET /api/crm/customers",
    "GET /api/crm/customers/:id",
    "GET /api/users",
    "GET /api/issuer-profiles",
    "GET /api/crm/billing-products",
    "GET /api/contracts/templates",
    "GET /api/crm/contracts/documents",
    "GET /api/marketing/contracts/history/:customer_id",
    "POST /api/operator/v0/customers/search",
    "POST /api/operator/v0/customers/context"
  ],
  "safe_write_or_low_risk": [
    "POST /api/crm/timeline",
    "PUT /api/crm/timeline/:id",
    "POST /api/operator/v0/activities/log",
    "POST /api/operator/v0/tasks"
  ],
  "preview_or_browser_apply": {
    "customer_profile_update": {
      "route": "PUT /api/crm/customers/:id",
      "confirmation_required": true
    },
    "contact_create_or_update": {
      "route": "POST /api/crm/contacts or PUT /api/crm/contacts/:id",
      "confirmation_required": true
    },
    "contract_create_or_update": {
      "route": "POST /api/crm/contracts or PUT /api/crm/contracts/:id",
      "confirmation_required": true
    },
    "contract_document_generate": {
      "route": "POST /api/crm/contracts/generate",
      "confirmation_required": true
    },
    "electronic_contract_send": {
      "route": "POST /api/marketing/contracts/:requestId/send",
      "confirmation_required": true
    },
    "invoice_quotation_receipt_create": {
      "route": "POST /api/crm/invoices",
      "confirmation_required": true
    },
    "invoice_status_update": {
      "route": "PUT /api/crm/invoices/:id/status",
      "confirmation_required": true
    },
    "customer_import_preview": {
      "route": "POST /api/crm/customers/import/preview",
      "confirmation_required": false
    },
    "customer_import_commit": {
      "route": "POST /api/crm/customers/import/jobs/:jobId/commit",
      "confirmation_required": true
    }
  },
  "agent_output": {
    "customer_summary": {
      "company_name": "string",
      "status": "active | inactive | churn_risk | unknown",
      "ltv": 0,
      "contract_summary": "string",
      "billing_summary": "string",
      "latest_timeline": "string"
    },
    "recommended_next_actions": [
      {
        "type": "timeline_note | contact_update | contract_review | invoice_draft | document_draft | task_create",
        "reason": "string",
        "requires_confirmation": true
      }
    ],
    "drafts": {
      "timeline": {
        "activity_type": "note",
        "summary": "string",
        "details": "string"
      },
      "invoice_or_quotation": {
        "type": "quotation | invoice | receipt",
        "items": [],
        "notes": "string"
      },
      "contract_document": {
        "template_id": "number",
        "variables": {}
      }
    },
    "warnings": [],
    "needs_confirmation": []
  },
  "confirmation_required": [
    "customer.create",
    "customer.update",
    "customer.bulk_update",
    "customer.import_commit",
    "contact.create",
    "contact.update",
    "contact.delete",
    "contract.create",
    "contract.update",
    "contract.delete",
    "contract_document.generate",
    "contract_document.delete",
    "electronic_contract.upload",
    "electronic_contract.send",
    "invoice.create",
    "invoice.status.update",
    "receipt.create",
    "subscription.create",
    "subscription.cancel",
    "stripe.payment_link.create",
    "issuer_profile.update",
    "billing_product.update"
  ],
  "dangerous_or_browser_preferred": [
    "subscription.create",
    "subscription.cancel",
    "stripe.payment_link.create",
    "electronic_contract.send",
    "customer.bulk_update",
    "customer.delete",
    "contract_document.delete",
    "invoice.status.paid"
  ]
}
\`\`\`

Rules:

- Use \`customer.read\` / \`customer.context.read\` before proposing contract, invoice, or follow-up actions.
- Timeline note drafts are the safest CRM write. Still show the note before saving.
- Do not create invoices, receipts, contract documents, electronic contracts, subscriptions, or payment links without human confirmation.
- Do not update Stripe, billing, subscription, invoice status, contract status, or bulk customer fields silently.
- Customer import must run preview first; commit requires confirmation.
- Prefer browser confirmation for electronic contract, Stripe subscription, payment, and document deletion flows.

## Agent Contract: marketing.lead_management

Lead management is the bridge between inbound/house/anonymous leads, AI traffic, access analytics, SFA conversion, and marketing distribution. The parent menu should be treated as \`マーケティング\`, not as a Meta-specific area.

Agent flow:

\`\`\`text
ユーザーが「このリードどうする？」「メルマガ誰に送る？」と依頼
↓
Agentがactive / house / anonymous / ai_traffic / access_analytics sourcesを読む
↓
Agentが案件化候補、育成候補、除外候補を整理
↓
AgentがCRM customer向けかlead/house list向けかを分けて宛先計画を作る
↓
状態変更、案件化、取込、配信対象反映は人間確認
↓
browser apply or API apply
\`\`\`

Contract:

\`\`\`json
{
  "screen": "marketing.lead_management",
  "mode": "agent_lead_triage_conversion_and_distribution_source_planning",
  "human_page": "/marketing/leads",
  "lead_categories": {
    "active": "広告・フォーム・Web流入などのアクティブリード",
    "house": "CSV取込・手動登録などのハウスリード",
    "anonymous": "匿名訪問者・visitor intelligence",
    "ai_traffic": "AI流入。ChatGPT / Claude / Gemini / Perplexity 等からの参照・巡回検知。SalesBreaker内検索機能ではない。",
    "access_analytics": "1行タグで取得したページ別・流入元別アクセス分析",
    "all": "統合リード一覧"
  },
  "read": [
    "GET /api/marketing/leads",
    "GET /api/marketing/leads/:id/timeline",
    "GET /api/marketing/anonymous-visitors",
    "GET /api/marketing/visitors/:visitor_id/timeline",
    "GET /api/marketing/ai-discovery",
    "GET /api/marketing/access-analytics",
    "GET /api/marketing/campaigns/:campaign_id/recipients",
    "POST /api/operator/v0/engagement/search",
    "POST /api/operator/v0/history/search",
    "POST /api/operator/v0/deals/search",
    "POST /api/operator/v0/customers/search"
  ],
  "preview_or_browser_apply": {
    "lead_update": {
      "route": "PATCH /api/marketing/leads/:id",
      "payload": {
        "company_name": "株式会社Example",
        "name": "山田 太郎",
        "email": "lead@example.com",
        "phone": "03-1234-5678",
        "status": "new | contacted | qualified | archived",
        "internal_memo": "フォロー方針、温度感、確認事項",
        "tags": ["採用", "セミナー参加"]
      },
      "confirmation_required": true
    },
    "lead_status_update": {
      "route": "PUT /api/marketing/leads/:id/status",
      "confirmation_required": true
    },
    "lead_import": {
      "route": "POST /api/marketing/leads/import",
      "confirmation_required": true
    },
    "lead_create": {
      "route": "POST /api/marketing/leads",
      "confirmation_required": true
    },
    "lead_score_recalculate": {
      "route": "POST /api/marketing/leads/score/calculate",
      "confirmation_required": true
    },
    "lead_to_deal": {
      "route": "POST /api/marketing/leads/:id/convert",
      "confirmation_required": true
    },
    "anonymous_visitor_confirm": {
      "route": "POST /api/marketing/visitors/confirm",
      "confirmation_required": true
    },
    "anonymous_visitor_note": {
      "route": "POST /api/marketing/visitors/note",
      "confirmation_required": true
    },
    "anonymous_visitor_to_deal": {
      "route": "POST /api/marketing/visitors/convert",
      "confirmation_required": true
    }
  },
  "mail_scenario_source_planning": {
    "email_campaign_target_filter": {
      "source_type": "lead | customer",
      "lead_filter": {
        "type": "all | segment",
        "tags": "string",
        "min_score": 0
      },
      "customer_filter": {
        "tags": "string",
        "contract_status": "active | pending | cancelled | completed | all"
      }
    },
    "source_rules": [
      "Use customer source for existing customers and CRM contacts.",
      "Use lead source for active leads, house leads, and nurture lists.",
      "Treat anonymous visitors as research signals until confirmed or converted.",
      "Do not silently mix CRM customers and leads in one campaign.",
      "If the user wants both CRM customers and house leads, create an explicit recipient plan with separate segments."
    ]
  },
  "agent_output": {
    "lead_summary": {
      "active_count": 0,
      "house_count": 0,
      "anonymous_count": 0,
      "ai_traffic_count": 0,
      "high_intent_count": 0
    },
    "ai_traffic_summary": {
      "latest_events": 0,
      "top_sources": ["ChatGPT", "Claude", "Gemini", "Perplexity"],
      "interpretation": "AI流入はAI回答・AI経由参照・AIクローラー経由の発見シグナル。問い合わせや案件化の根拠にする前にページ閲覧、フォーム送信、クリック、CRM履歴と合わせて確認する。"
    },
    "access_analytics_summary": {
      "page_views": 0,
      "visitors": 0,
      "top_pages": [],
      "top_sources": [],
      "recommendation": "1行タグが未設置またはイベントが少ない場合、まずタグ設置・LP導線・CTAを確認する。"
    },
    "conversion_candidates": [
      {
        "lead_id": "number",
        "reason": "clicked recently, high score, form submitted",
        "recommended_deal_action": "create_deal | attach_to_existing_deal | keep_nurture"
      }
    ],
    "mail_recipient_plan": {
      "source_type": "lead | customer",
      "segment_rule": "string",
      "expected_count": 0,
      "excluded_count": 0,
      "needs_confirmation": true
    },
    "scenario_recipient_plan": {
      "source_type": "lead | customer",
      "trigger": "tag | score | form | manual",
      "needs_confirmation": true
    },
    "warnings": [],
    "needs_confirmation": []
  },
  "confirmation_required": [
    "lead.create",
    "lead.update",
    "lead.status.update",
    "lead.import",
    "lead.score_recalculate",
    "lead.convert_to_deal",
    "anonymous_visitor.confirm",
    "anonymous_visitor.note",
    "anonymous_visitor.convert_to_deal",
    "mail_campaign.target_source_apply",
    "scenario.target_source_apply",
    "scenario.activate"
  ],
  "phase2_or_blocked": [
    "mail.send",
    "scenario.send",
    "scenario.activate_without_preview",
    "bulk_status_update_without_preview",
    "anonymous_to_deal_without_confirmation",
    "meta_ad_analysis_deep_dive"
  ]
}
\`\`\`

Rules:

- Active leads can become deal candidates, but conversion requires human confirmation.
- House leads should be normalized with tags before mail or scenario targeting.
- Anonymous visitors are not mail targets until confirmed or converted.
- \`AI流入\` means AI referral/crawler traffic, not an internal AI search box. Treat it as a free read/analyze signal that helps users justify installing the one-line tag.
- Access analytics should be used to diagnose page/source behavior before claiming that a campaign or LP is underperforming.
- For email campaigns, explicitly choose \`source_type\` as \`lead\` or \`customer\` before applying target filters.
- Do not silently mix CRM customers and leads in one campaign. If both are needed, create a recipient plan with separate segments.
- Archived leads should be excluded from recipient plans unless the user explicitly asks otherwise.
- Do not start \`mail.send\` or \`scenario.send\` from this screen; prepare preview and confirmation only.

## Agent Contract: marketing.email_campaigns

Email campaign management is close to send execution, but the Agent's normal scope is drafting, previewing, recipient planning, and test-send preparation. Existing front-end Gem links and HTML prompt helpers can remain; the external Agent can also generate the HTML body directly.

Agent flow:

\`\`\`text
ユーザーが「この内容でメルマガ作って」と依頼
↓
Agentが送信元、既存キャンペーン、利用上限、リード/顧客セグメントを読む
↓
Agentが件名、HTML本文、CTA、宛先条件、テスト送信計画を作る
↓
previewでHTML表示崩れ、URL、フッター重複、宛先条件、残数を確認
↓
下書き保存/更新は人間確認
↓
テスト送信は人間確認
↓
即時/予約配信は有料プラン + 確認付き実行Gate
\`\`\`

Contract:

\`\`\`json
{
  "screen": "marketing.email_campaigns",
  "mode": "agent_html_newsletter_draft_target_preview",
  "human_page": "/marketing/email",
  "role": "Email campaign authoring is close to send execution. Agent can draft, preview, plan recipients, and prepare test-send. Real delivery stays behind 有料Gate.",
  "browser_helpers": [
    "専用Gemリンクは既存フロントのまま残す",
    "HTMLメール作成アシストは既存フロントのまま残す",
    "Agent can create HTML directly without requiring the user to copy prompts into Gemini"
  ],
  "read": [
    "GET /api/marketing/campaigns",
    "GET /api/marketing/campaigns/:campaign_id/recipients",
    "GET /api/marketing/campaigns/:campaign_id/report",
    "GET /api/issuer-profiles",
    "GET /api/usage-status",
    "POST /api/operator/v0/engagement/search",
    "POST /api/operator/v0/customers/search"
  ],
  "preview_or_browser_apply": {
    "campaign_draft_save": {
      "route": "POST /api/marketing/campaigns or PUT /api/marketing/campaigns/:id",
      "payload": {
        "title": "2026-06 採用広報メルマガ",
        "issuer_profile_id": 123,
        "sender_name": "株式会社Example",
        "subject": "採用広報の新しい打ち手について",
        "body": "<table role=\\"presentation\\" width=\\"100%\\">...</table>",
        "target_filter": {
          "source_type": "lead | customer",
          "type": "all | segment",
          "tags": "採用,セミナー参加",
          "min_score": 60,
          "contract_status": "active | pending | cancelled | completed | all"
        }
      },
      "confirmation_required": true
    },
    "test_send": {
      "route": "POST /api/marketing/campaigns/test-send",
      "payload": {
        "subject": "string",
        "body": "html string",
        "target_email": "reviewer@example.com",
        "issuer_profile_id": 123
      },
      "confirmation_required": true
    },
    "delivery_start": {
      "route": "POST /api/marketing/campaigns/:id/send",
      "phase": "有料Gate",
      "confirmation_required": true
    },
    "delivery_cancel": {
      "route": "POST /api/marketing/campaigns/:id/cancel",
      "confirmation_required": true
    },
    "campaign_delete": {
      "route": "DELETE /api/marketing/campaigns/:id",
      "confirmation_required": true
    }
  },
  "html_rules": {
    "layout": "table layout for email clients",
    "css": "inline styles only",
    "max_width": "600px",
    "images": "avoid unless the user provides stable public image URLs",
    "footer": "do not create sender footer, company address, or unsubscribe footer; SalesBreaker appends them",
    "tracking": "do not add external tracking scripts or pixels",
    "tokens": ["{{会社名}}", "{{担当者名}}"],
    "cta": [
      {
        "label": "資料を見る",
        "url": "https://example.com/lp"
      }
    ]
  },
  "agent_output": {
    "campaign_draft": {
      "title": "string",
      "subject": "string",
      "body_html": "email-safe html string",
      "cta_urls": []
    },
    "recipient_plan": {
      "source_type": "lead | customer",
      "segment_rule": "string",
      "expected_count": 0,
      "excluded_count": 0,
      "needs_confirmation": true
    },
    "preflight": {
      "issuer_profile_ready": true,
      "usage_remaining": 0,
      "html_warnings": [],
      "target_warnings": []
    },
    "test_send_plan": {
      "target_email": "reviewer@example.com",
      "requires_confirmation": true
    },
    "warnings": [],
    "needs_confirmation": []
  },
  "confirmation_required": [
    "email_campaign.create",
    "email_campaign.update",
    "email_campaign.delete",
    "email_campaign.target_filter.apply",
    "email_campaign.test_send",
    "email_campaign.schedule",
    "email_campaign.cancel_schedule",
    "email_campaign.send"
  ],
  "phase2_or_blocked": [
    "mail.send",
    "email_campaign.immediate_send_by_agent",
    "email_campaign.scheduled_send_by_agent",
    "recipient_bulk_mix_without_preview",
    "usage_limit_bypass",
    "send_without_test_or_preview"
  ]
}
\`\`\`

Rules:

- Default to \`delivery_type=draft\` when the Agent creates or updates a campaign.
- Agent may generate complete HTML email body directly.
- Do not include sender footer, company address, or unsubscribe link in generated HTML. SalesBreaker appends those.
- Use table layout and inline CSS for email compatibility.
- Explicitly choose \`source_type\` as \`lead\` or \`customer\` before applying recipient filters.
- Check issuer profile and usage remaining before suggesting test send or delivery.
- Test send requires confirmation and a reviewer email.
- Immediate send, scheduled send, and send automation remain 有料Gate.

## Agent Contract: marketing.scenario_delivery

Scenario delivery connects forms, tags, existing email campaigns, wait times, and engagement conditions. This is a page where humans can easily get stuck, so the Agent should translate the automation into a simple flow before anything is saved or activated.

Agent flow:

\`\`\`text
ユーザーが「このリードにステップメール組みたい」と依頼
↓
Agentが既存シナリオ、フォーム、メールキャンペーン、反応履歴を読む
↓
Agentがトリガー、待機時間、条件分岐、使用メールを整理
↓
Agentが人間に読めるフローへ変換してpreviewする
↓
不足メールがあればmarketing.email_campaignsで下書き
↓
保存は人間確認
↓
有効化/自動送信は有料プラン + 確認付き実行Gate
\`\`\`

Contract:

\`\`\`json
{
  "screen": "marketing.scenario_delivery",
  "mode": "agent_scenario_planner_and_activation_preflight",
  "human_page": "/marketing/scenarios",
  "role": "Scenario delivery connects forms, tags, existing email campaigns, wait times, and engagement conditions. Agent should make the flow understandable before any activation.",
  "read": [
    "GET /api/marketing/scenarios",
    "GET /api/marketing/scenarios/:id",
    "GET /api/marketing/forms",
    "GET /api/marketing/campaigns",
    "GET /api/marketing/campaigns/:campaign_id/report",
    "POST /api/operator/v0/engagement/search",
    "POST /api/operator/v0/history/search"
  ],
  "trigger_types": {
    "form_submission": {
      "description": "フォーム送信時",
      "requires": ["form_id"]
    },
    "tag_added": {
      "description": "リード/顧客へタグが付与された時",
      "requires": ["tag_name"]
    },
    "manual": {
      "description": "手動登録のみ",
      "requires": []
    },
    "meta_ad_submission": {
      "description": "Meta広告フォーム受信時",
      "note": "Existing screen supports this, but deep Meta operation is not the current Agent focus."
    }
  },
  "step_model": {
    "wait_time_hours": 24,
    "action_type": "send_email",
    "campaign_id": 123,
    "condition_type": "always | if_opened | if_not_opened | if_clicked | if_not_clicked",
    "condition_reference_step_order": 1
  },
  "preview_or_browser_apply": {
    "scenario_create": {
      "route": "POST /api/marketing/scenarios",
      "confirmation_required": true
    },
    "scenario_update": {
      "route": "PUT /api/marketing/scenarios/:id",
      "confirmation_required": true
    },
    "scenario_delete": {
      "route": "DELETE /api/marketing/scenarios/:id",
      "confirmation_required": true
    },
    "scenario_activate": {
      "field": "is_active = true",
      "route": "POST or PUT /api/marketing/scenarios",
      "phase": "有料Gate",
      "confirmation_required": true
    }
  },
  "agent_output": {
    "scenario_brief": {
      "title": "string",
      "goal": "string",
      "audience_source": "lead | customer | manual segment",
      "trigger": {
        "type": "form_submission | tag_added | manual | meta_ad_submission",
        "detail": "string"
      }
    },
    "steps": [
      {
        "order": 1,
        "wait_time_hours": 0,
        "condition": "always",
        "campaign_id": 123,
        "campaign_title": "string",
        "purpose": "first follow-up"
      }
    ],
    "preflight": {
      "all_campaigns_exist": true,
      "forms_exist": true,
      "no_zero_hour_chain_risk": true,
      "no_missing_campaign_id": true,
      "activation_safe": false
    },
    "human_readable_flow": [
      "フォームA送信後すぐにメール1を送る",
      "24時間後、未クリックならメール2を送る"
    ],
    "warnings": [],
    "needs_confirmation": []
  },
  "confirmation_required": [
    "scenario.create",
    "scenario.update",
    "scenario.delete",
    "scenario.activate",
    "scenario.deactivate",
    "scenario.step.add",
    "scenario.step.remove",
    "scenario.trigger.change",
    "scenario.condition.change"
  ],
  "phase2_or_blocked": [
    "scenario.send",
    "scenario.activate_without_preview",
    "scenario.bulk_activate",
    "scenario.delete_active_without_stop",
    "mail.send",
    "meta_ad_analysis_deep_dive"
  ]
}
\`\`\`

Rules:

- Agent should translate scenario logic into a human-readable flow before saving.
- Use existing email campaigns as step content; draft missing emails through \`marketing.email_campaigns\` first.
- Do not activate a scenario in the same step as drafting unless the human explicitly confirms activation.
- Every step must have \`campaign_id\` and \`wait_time_hours\`.
- Condition references must point to an earlier step.
- Warn when multiple zero-hour steps could send too many emails too quickly.
- Deleting an active scenario requires stop/deactivate confirmation first.
- Scenario activation and actual automated sending remain 有料Gate.

## Agent Contract: marketing.forms_and_lp_hosting

Forms and LP hosting should be treated as one workflow. Forms collect lead data and attach tags; LP hosting publishes the page. The original reason for this surface is that users often get stuck on AI-generated HTML hosting. If SalesBreaker handles hosting, the Agent can focus on conversion copy, form fields, tags, and publish preflight.

Agent flow:

\`\`\`text
ユーザーがLPやフォームの目的を伝える
↓
Agentがフォーム項目、タグ、自動返信、完了後導線を作る
↓
AgentがLP HTMLを一体生成する
↓
LPにフォーム連携とタグ設計が入っているか確認
↓
previewで見た目、画像、URL、必須項目、タグ、公開状態を確認
↓
人間確認
↓
browser apply or API apply
\`\`\`

Contract:

\`\`\`json
{
  "screen": "marketing.forms_and_lp_hosting",
  "mode": "agent_form_lp_cross_surface_builder",
  "human_pages": [
    "/marketing/forms",
    "/marketing/lps"
  ],
  "role": "Forms collect lead data. LP hosting publishes the page. Agent should design them together so form fields, tags, auto-reply, tracking, and scenario handoff stay consistent.",
  "read": [
    "GET /api/marketing/forms",
    "GET /api/marketing/lps",
    "GET /api/marketing/lps/:id",
    "GET /api/marketing/lps/:id/images",
    "GET /api/issuer-profiles",
    "GET /api/usage-status",
    "POST /api/operator/v0/engagement/search"
  ],
  "preview_or_browser_apply": {
    "form_create_or_update": {
      "route": "POST /api/marketing/forms (include id for update)",
      "payload": {
        "title": "資料請求フォーム",
        "display_title": "資料請求・お問い合わせ",
        "is_external_api_mode": false,
        "is_active": true,
        "notification_email": "admin@example.com",
        "is_auto_reply_enabled": true,
        "issuer_profile_id": 123,
        "auto_reply_subject": "お問い合わせありがとうございます",
        "auto_reply_body": "{{お名前}} 様\\\\nお問い合わせありがとうございます。",
        "success_message": "お問い合わせありがとうございます。",
        "redirect_url": "https://example.com/thanks",
        "fields_config": [
          {
            "id": "company",
            "type": "text",
            "label": "会社名",
            "required": true
          },
          {
            "id": "name",
            "type": "text",
            "label": "お名前",
            "required": true
          },
          {
            "id": "email",
            "type": "email",
            "label": "メールアドレス",
            "required": true
          }
        ],
        "style_config": {
          "theme": "blue",
          "button_text": "この内容で送信する"
        }
      },
      "confirmation_required": true
    },
    "lp_create_or_update": {
      "route": "POST /api/marketing/lps for create, PUT /api/marketing/lps/:id for update",
      "payload": {
        "title": "採用広報 LP",
        "slug": "recruiting-lp",
        "linked_form_id": 123,
        "is_active": true,
        "html_content": "<!DOCTYPE html><html lang=\\"ja\\"><head><style>...</style></head><body>...</body></html>",
        "css_content": "",
        "js_content": "",
        "head_tags": "",
        "og_title": "採用広報 LP",
        "og_description": "資料請求はこちら",
        "publish_start_at": "",
        "publish_end_at": ""
      },
      "confirmation_required": true
    },
    "lp_image_upload": {
      "route": "POST /api/marketing/lps/:id/images",
      "confirmation_required": true
    },
    "form_delete": {
      "route": "DELETE /api/marketing/forms/:id",
      "confirmation_required": true
    },
    "lp_delete": {
      "route": "DELETE /api/marketing/lps/:id",
      "confirmation_required": true
    }
  },
  "form_field_policy": {
    "required_baseline": ["company", "name", "email"],
    "recommended_optional": ["phone", "message"],
    "field_types": ["text", "textarea", "email", "tel", "date", "radio", "checkbox", "select"],
    "tag_policy": [
      "Agent must propose lead tags before form/LP publish.",
      "Tags should support later lead segmentation, mail targeting, and scenario triggers.",
      "Do not create vague tags such as 未分類 or テスト unless explicitly requested."
    ]
  },
  "lp_html_policy": {
    "single_source": "html_content",
    "css_rule": "Put CSS in <style> inside html_content. Do not use separate css_content.",
    "js_rule": "Put necessary JS in <script> inside html_content only when needed. Do not use separate js_content.",
    "form_rule": "Prefer a native HTML <form> with standard field names that the SalesBreaker one-line script can detect. Use {{form}} only when iframe-style embedding is explicitly requested.",
    "tracking_rule": "Do not add Meta Pixel, CAPI, or SalesBreaker tracking scripts manually unless instructed by the tracking page contract.",
    "hosting_rule": "Agent does not need to solve hosting; SalesBreaker LP hosting publishes the HTML."
  },
  "agent_output": {
    "form_plan": {
      "purpose": "資料請求 | 問い合わせ | セミナー申込",
      "fields": [],
      "tags": [],
      "auto_reply": {
        "subject": "string",
        "body": "string"
      },
      "redirect_url": "string"
    },
    "lp_plan": {
      "title": "string",
      "slug": "string",
      "linked_form_id": "number or pending",
      "html_content": "complete HTML",
      "og": {
        "title": "string",
        "description": "string"
      }
    },
    "preflight": {
      "form_linked": true,
      "required_fields_present": true,
      "tags_ready": true,
      "auto_reply_ready": true,
      "no_separate_css_js": true,
      "publish_safe": false
    },
    "warnings": [],
    "needs_confirmation": []
  },
  "confirmation_required": [
    "form.create",
    "form.update",
    "form.delete",
    "form.external_tag_apply",
    "form.auto_reply.update",
    "lp.create",
    "lp.update",
    "lp.publish",
    "lp.unpublish",
    "lp.delete",
    "lp.image_upload",
    "lp.slug.change"
  ],
  "phase2_or_blocked": [
    "separate_css_content_field",
    "separate_js_content_field",
    "manual_tracking_script_insert",
    "meta_ad_analysis_deep_dive",
    "publish_without_preview",
    "delete_without_impact_check"
  ]
}
\`\`\`

Rules:

- Treat form and LP as one workflow.
- Agent should create the form plan before final LP publish.
- Tags must be useful for lead management, mail targeting, and scenario triggers.
- LP hosting means Agent can focus on HTML and conversion copy, not infrastructure.
- Do not use separate \`css_content\` or \`js_content\` fields; put everything inside \`html_content\`.
- Publishing, unpublishing, deleting, slug changes, external tag application, and auto-reply changes require human confirmation.

## Agent Contract: marketing.presentation_builder

資料作成は「1つの巨大なHTML資料を作る場所」ではなく、Agentが1ページずつHTMLスライドを作り、人間があとから順番、文言、グラフ、画像、細部を直せるようにする画面として扱う。

Agent flow:

\`\`\`text
ユーザーが資料目的と相手を伝える
↓
Agentが構成案とページごとの役割を作る
↓
Agentが1ページずつ raw_html を生成する
↓
人間がページ順、文言、画像、グラフを調整する
↓
previewでページID、グラフID、画像パス、ページ番号を確認する
↓
人間確認
↓
PDF出力または共有
\`\`\`

Contract:

\`\`\`json
{
  "screen": "marketing.presentation_builder",
  "mode": "agent_page_by_page_html_slide_builder",
  "human_page": "/marketing/presentations",
  "role": "This page exists because HTML-based documents are hard to fix, reorder, and fine-tune when they are one large blob. Agent should create one HTML page at a time, then the human can reorder, edit text, adjust charts, replace images, and export.",
  "read": [
    "GET /api/docs",
    "GET /api/docs/:doc_id/pages",
    "GET /api/client-info",
    "GET /api/usage-status",
    "GET /api/marketing/lps/:id/images"
  ],
  "preview_or_browser_apply": {
    "document_create": {
      "route": "POST /api/docs/save",
      "payload": {
        "title": "A社向け提案資料",
        "slug": "proposal-a",
        "category": "sales | external | internal | simulation | bank_plan | seminar",
        "metadata": {
          "target_url": "https://example.com",
          "proposal_points": "提案の核を箇条書き"
        }
      },
      "confirmation_required": true
    },
    "page_save": {
      "route": "POST /api/docs/pages/save",
      "payload": {
        "doc_id": 123,
        "page_id": 456,
        "order_index": 0,
        "raw_html": "<section id=\\"page-1\\" style=\\"width:1122px;height:793px\\">...</section>"
      },
      "confirmation_required": false
    },
    "page_reorder": {
      "route": "POST /api/docs/pages/reorder",
      "payload": {
        "doc_id": 123,
        "orders": [
          { "id": 456, "index": 0 },
          { "id": 789, "index": 1 }
        ]
      },
      "confirmation_required": true
    },
    "pdf_export": {
      "route": "POST /api/docs/:doc_id/export-pdf",
      "confirmation_required": true
    }
  },
  "page_html_contract": {
    "page_size": "A4 landscape, 1122x793",
    "unit": "one raw_html per page",
    "page_id_rule": "Only include the current page container, e.g. id=\\"page-1\\". Do not include other page containers.",
    "page_number_rule": "Use {{page_num}} / {{total_pages}} placeholders.",
    "chart_rule": "Use chart-{{page_num}} or a chart id matching the current page number.",
    "image_rule": "Use img/filename paths for uploaded assets.",
    "font_rule": "Use Japanese-safe fonts and readable sizes.",
    "animation_rule": "No animation for proposal materials."
  },
  "agent_output": {
    "document_brief": {
      "title": "string",
      "category": "sales | external | internal | simulation | bank_plan | seminar",
      "target_url": "string",
      "proposal_points": []
    },
    "outline": [
      {
        "page": 1,
        "title": "課題提起",
        "purpose": "target pain and urgency",
        "requires_chart": false,
        "requires_image": false
      }
    ],
    "pages": [
      {
        "order_index": 0,
        "raw_html": "complete single-page HTML fragment",
        "edit_notes": "what the human may want to adjust",
        "warnings": []
      }
    ],
    "preflight": {
      "all_pages_have_html": true,
      "page_ids_valid": true,
      "chart_ids_valid": true,
      "image_paths_valid": true,
      "export_ready": false
    }
  },
  "confirmation_required": [
    "document.create",
    "document.delete",
    "document.duplicate",
    "page.add",
    "page.delete",
    "page.reorder",
    "media.upload",
    "media.delete",
    "pdf.export",
    "public_share_or_publish"
  ],
  "phase2_or_blocked": [
    "one_giant_document_html",
    "delete_without_confirmation",
    "export_without_preview",
    "unapproved_external_script",
    "automatic_public_share_without_confirmation"
  ]
}
\`\`\`

Rules:

- Agent should create and revise one page at a time.
- Keep each page as independently editable \`raw_html\`.
- Do not mix multiple slide pages into a single \`raw_html\` field.
- Run page validation before export: page id, chart id, image paths, and page number placeholders.
- PDF export, page delete, document delete, media delete, and public sharing require human confirmation.
`,
  },
  {
    id: "agent-operating-manual",
    title: "Agent Operating Manual",
    filename: "salesbreaker-agent-operating-manual.md",
    audience: "Agent",
    summary: "Codex / Claude Codeが最初に読む運用Manual。",
    content: `# SalesBreaker Agent Operating Manual

このManualは、Codex / Claude Codeなどの信頼済みAIツールがSalesBreakerを扱うときの作業手順です。

## Workspace

\`\`\`text
SalesBreaker/
├ docs/
├ strategy/
├ campaigns/
├ saved-lists/
├ templates/
├ analytics/
├ reports/
└ logs/
\`\`\`

## Standard Flow

\`\`\`text
営業戦略
↓
企業検索 / クレンジング済みリスト
↓
送付先リスト確認
↓
文面下書き / 送信前確認
↓
人間の承認
↓
実行
↓
結果分析
↓
CRMフォロー
\`\`\`

## Approval Rule

AIツールは提案する。SalesBreakerは送信前確認を作る。人間が承認する。承認後にだけ実行する。SalesBreakerは証跡を残す。AIツールは結果を分析して改善案を出す。

送信前確認を、人間の承認なしに実行へ進めないでください。

## Agent Operation Boundary

Agentの基本役割は、準備、分析、記録、下書き、設定補助です。送信、公開、契約、請求、権限変更などの確定操作は、原則として人間がブラウザで確認してから進めます。

APIキーでブラウザログインなしに扱えるrouteがある場合でも、それは公開APIではありません。必ず \`Authorization: Bearer <salesbreaker_api_key>\` を使い、APIキーに許可されたtenantとscopeの範囲だけで操作してください。

| Class | Agentがしてよいこと | 人間確認 |
| --- | --- | --- |
| API read / analyze | 履歴、クリック、AI流入、設定、案件、顧客、リードを読み、分析や結合用メモを作る | 原則不要 |
| API draft / assist write | 会社概要、NG、送信除外、文面、活動ログ、下書き、設定案を作る | 保存・削除・一括変更は必要 |
| Browser confirm / execute | 送信、公開、配信開始、シナリオ有効化、契約、請求、電子契約、DNS確認 | 必須 |
| Out of normal scope | アカウント管理、ユーザー管理、決済、権限、APIキー発行、管理者設定 | Agentは通常触らない |

ユーザーがAgentにブラウザ操作で実行ボタンを押すよう明示した場合でも、Agentは直前に対象、件数、文面、URL、送信元、除外条件、副作用を要約し、ユーザー責任の確定操作であることを確認してください。

## Screen Handling Rules

| Screen / Feature | Agent handling rule |
| --- | --- |
| 会社概要＆NG設定 | 会社概要は必ず埋める。フォーム側の必須項目に電話、住所、担当者名、メールがあると、会社概要の不足で送信できないことがある。送信元/スタッフメールはサンクスメールや返信が多く届くため、用途を分けたアドレスを推奨する。ただしフリーアドレスは非推奨。NGリストと送信除外時間がある場合は最初に整える。 |
| 文面テンプレート | アポ取得ならLP、資料、日程調整などの次アクションURLをできるだけ入れる。ウェビナー告知、認知、資料案内など施策目的に合わせ、マーケティング思考で短く作る。URLなしの認知施策もあり得るが、理由を明示する。長い会社紹介や複数URLの詰め込みは避ける。 |
| リストダウンロード | リストが必要な場合に使う読み取り/取得系の画面。Agentは品質、件数、対象条件、除外理由を整理する。英語版で閉じている場合は無理に使わない。 |
| マイリスト管理 | ユーザー持ち込みリストは、会社名、URL、メール、電話、タグなどを整理してから入れる。Agent経由ならCSVの列ズレを避けるため、可能な範囲でJSON化してAPI投入する方が安全。ただし公開scopeやunlock gateがない場合は無理に実行しない。 |
| 送付先リスト | Agentがユーザーの好み、業種、地域、タグ、送信履歴、NG、クリック履歴をもとに作る中心画面。件数preview、サンプル、除外理由、分割案を出してから保存する。送付先リスト作成は送信実行ではない。 |
| 送信 | 設定やpreview作成はAgentが補助してよい。実送信ボタンは原則人間が押す。ユーザーがAgentにブラウザ操作で実行させる場合はユーザー責任として、直前に送信元、対象件数、文面、URL、除外、速度、使用枠を確認する。 |
| 送信履歴 | APIで引っ張って分析する。送信成功、失敗理由、クリック、Web訪問、メールfallback、案件、顧客履歴をAgent側で結合し、次アクションに落とす。 |
| 案件管理 | 見込み管理の中心。商談結果、架電結果、温度感、次アクション、ステータス変更はAgentがAPIで記録/下書きできる。ステータス更新や見積発行など副作用のある操作はpreviewと確認を挟む。 |
| 受注一覧 | 基本はread-onlyまたは後回し。Agentの通常運用では重要度は低い。 |
| 顧客台帳 CRM | 機能が多いため慎重に扱う。対応履歴、メモ、タスク、顧客状況の整理はAPIで相性がよい。請求書、領収書、契約書、電子契約、Stripe/決済、金額確定、契約状態変更は人間がブラウザで確認する。 |
| クリックログ | 送信履歴と同じく分析対象。複数クリック、資料クリック、LP遷移、AI流入、Web訪問を結合し、案件化/フォロー候補を出す。 |
| 売上分析 | 中途半端な画面として扱い、Agent通常運用では優先しない。必要なら外部CSVやCRM/注文データと結合して分析する。 |
| リード管理 | 分析、分類、案件化候補の抽出、メモ、タグ、状態変更案を扱う。案件化や配信対象への反映は確認付き。匿名訪問者やAI流入は、そのまま売上根拠にせず、フォーム、注文、CRM、CSVと結合して判断する。 |
| メール配信 | 件名、HTML本文、CTA、宛先条件、テスト送信計画、下書き保存まではAgent補助でよい。実配信、予約配信、配信開始は必ずブラウザ確認と人間承認を挟む。 |
| シナリオ配信 | Agentがトリガー、待機時間、条件分岐、使用メールを設計し、人間に読めるフローへ変換する。保存は確認付き、有効化や自動送信開始は人間確認。 |
| フォーム作成 | Agentがフォーム項目、タグ、自動返信、通知先、完了後導線を作る。公開前にブラウザで必須項目、通知、自動返信、タグ、完了導線を確認する。 |
| LP制作・公開 | AgentがHTML、コピー、フォーム連携、OGP、CTAを作る。公開、差し替え、本番反映、外部タグ設置完了判断はブラウザ/人間確認を挟む。 |
| 資料作成 | Agentが構成とページ単位のHTMLを作る。PDF出力、公開共有、削除、並び替えは人間確認。 |
| マーケティング設定 | タグ、ドメイン、CAPI、計測、連携設定はAgentが確認/下書きしてよい。DNSや外部サーバーはユーザー環境依存なので、最終確認は人間が行う。1行タグの設置有無は分析前に確認する。 |
| アカウント/ユーザー/決済/権限/APIキー発行 | 管理側の領域。Agentの通常運用対象から外す。必要がある場合も、原則ブラウザで人間が操作する。 |

## New Project Setup Runbook

ユーザーが「プロジェクト作ったけど、次どうしたらいい？」と聞いた場合、Agentは以下の順で初期設定を進めます。

\`\`\`text
1. 会社概要を埋める
2. 送信元メールとSender DNS readinessを確認する
3. DKIM CNAME 3件を案内する
4. tracking CNAME 1件を案内する
5. DNS/SSL/route readinessを確認する
6. 1行タグを必要ページに設置する
7. LP/フォーム/サンクスページを必要なら作る
8. 文面テンプレートを作る
9. NGリスト/送信除外時間を整える
10. 送付先リストを作る
11. 送信前previewを出す
12. 人間確認後にだけ実行する
13. 48時間後にクリック/反応/案件化を分析する
\`\`\`

サーバー、DNS、ドメイン管理画面、LP設置先など、ユーザーの外部環境に依存する作業は必ずユーザーへ確認してください。SalesBreaker内で入力・整理できる作業はAgentが進めてよいですが、DNS反映完了や送信実行を勝手に仮定しないでください。

## Domain / DNS Readiness

SalesBreakerでは、App Domain、Sender Domain、Tracking Domainを分けて扱います。

\`\`\`json
{
  "app_domain": {
    "purpose": "SalesBreaker SaaS, login, API",
    "default": "salesbreaker.jp"
  },
  "sender_domain": {
    "purpose": "sender authentication",
    "basic_records": ["DKIM CNAME x3"],
    "future_optional_records": ["SPF", "DMARC", "custom MAIL FROM"]
  },
  "tracking_domain": {
    "purpose": "click tracking, open tracking, unsubscribe URLs",
    "default": "track.sbroute.net",
    "custom_example": "track.client-domain.com",
    "ready_when": ["dns_status=valid", "ssl_status=issued", "route_status=ok", "active_flag=1"]
  }
}
\`\`\`

Agentは、送信前や配信不調の相談時に以下を確認します。

- 会社概要が十分に埋まっているか。
- 送信元メールが存在し、認証状態が確認できるか。
- DKIM CNAME 3件が案内済み/設定済みか。
- tracking CNAME 1件が案内済み/設定済みか。
- 現在のtracking domainが \`track.sbroute.net\` か、顧客custom domainか。
- templateにURL/LP/資料/日程調整などの次アクションがあるか。
- 送信除外時間とNGリストが整っているか。

\`salesbreaker.jp\` は本体SaaS/API/ログイン用です。メール内のクリック、開封、配信停止URLについて、Agentは \`salesbreaker.jp\` をtracking domainと仮定しないでください。

## Logs To Keep

- 営業戦略メモ。
- リスト確認結果。
- 件数、照合情報、版情報。
- テンプレート下書き。
- 人間の承認内容。
- 実行リクエストの要約。
- 分析結果と次アクション。
- 失敗、拒否、事故未遂の記録。

## RevoComi Pre-Release Validation

RevoComi is the primary pre-release validation account when the owner explicitly allows live checks.

\`\`\`json
{
  "validation_client": {
    "client_id": "client001",
    "company_name": "株式会社RevoComi",
    "purpose": "pre-release validation for Agent/browser/API-assisted SalesBreaker operations"
  },
  "rules": [
    "Use visible test names, tags, memo text, and timestamps so validation data can be found later.",
    "Prefer create -> read -> update -> read -> cleanup when a feature supports safe cleanup.",
    "Do not run send execution unless the owner explicitly asks for that send test.",
    "For read/preview operations, record the route, request shape, summary result, and any UI mismatch.",
    "For writes, record before/after state and whether the browser screen reflects the API result."
  ],
  "stop_conditions": [
    "unexpected cross-client data",
    "raw API key or Authorization header appears in response/log/UI",
    "send/job/usage tables change during a non-send validation",
    "browser screen and API state disagree after refresh",
    "operation requires user/billing/admin permission not already approved"
  ]
}
\`\`\`

## MyPage Result Review Manual

MyPage is where humans notice active jobs, recent send outcomes, fallback candidates, and hot engagement signals. Agents should not merely repeat the numbers; they should explain what the numbers mean, what they do not mean, and what to do next.

### Standard Result Reading

\`\`\`json
{
  "input_sources": [
    "send job summary",
    "send history",
    "click logs",
    "web tracking logs",
    "mail fallback events",
    "deal/customer context when needed"
  ],
  "interpretation_rules": {
    "SUCCESS": "The form/email operation reached the intended delivery path. It does not mean the prospect replied or became a deal.",
    "NEEDS_REVIEW": "Likely reached a form or ambiguous state; inspect sample rows before treating as success or failure.",
    "NO_FORM_FOUND": "Usually list/contact-url quality or website structure issue, not a copywriting result.",
    "DEAD_URL": "URL/list quality issue. Clean or replace targets before judging the campaign.",
    "WRONG_FORM_TYPE": "The page existed but was not an appropriate sales/contact form. Improve target/contact URL quality.",
    "SALES_REJECTED": "The site or policy rejected sales contact. Treat as exclusion/NG learning, not copy failure.",
    "FAIL": "Technical or unknown failure; inspect samples and logs before making a campaign-level conclusion.",
    "clicks": "Engagement signal. Multiple clicks by the same company should move to follow-up/deal/task consideration."
  },
  "timing_rules": {
    "immediate": "Check delivery/failure breakdown and obvious hot clicks.",
    "after_24_hours": "Check early clicks and replies.",
    "after_48_hours": "Preferred timing for first serious engagement analysis.",
    "after_7_days": "Use for template/list performance comparison."
  },
  "agent_output_shape": {
    "plain_language_summary": "Short human-readable assessment.",
    "numbers": {
      "target_count": 0,
      "success_count": 0,
      "needs_review_count": 0,
      "unreachable_count": 0,
      "click_count": 0,
      "hot_lead_count": 0
    },
    "what_looks_good": [],
    "what_needs_review": [],
    "what_not_to_conclude_yet": [],
    "recommended_next_actions": [],
    "suggested_message_to_user": "string"
  }
}
\`\`\`

### Practical Appointment Benchmarks

Form outreach conversion varies heavily by offer, copy, target quality, timing, brand, market, and whether the message sends users to a strong LP or material. Agents must treat the following as rough operating heuristics, not guarantees.

\`\`\`json
{
  "benchmark_policy": {
    "primary_denominator": "successful_form_or_email_delivery_count",
    "do_not_use_as_primary_denominator": "raw_target_count",
    "very_good_case": {
      "appointment_rate": "1 appointment per about 1,000 successful deliveries",
      "interpretation": "Strong offer, clear target, good copy/LP, or urgent market need."
    },
    "normal_case": {
      "appointment_rate": "1 appointment per about 10,000 successful deliveries",
      "interpretation": "Common for broad cold outreach. Do not panic from low early appointment counts."
    },
    "early_result_warning": {
      "minimum_review_window": "48 hours for first engagement review; 7 days for stronger comparison",
      "reason": "Clicks, replies, internal sharing, and follow-up behavior lag behind delivery."
    }
  },
  "diagnosis_rules": {
    "low_success_rate": "Likely list/contact_url/form compatibility problem before copy problem.",
    "high_success_low_click": "Likely offer/copy/CTA/target mismatch. Improve first sentence, URL, LP, or segment.",
    "clicks_but_no_appointment": "Follow up hot leads and improve LP/material conversion.",
    "many_wrong_form_type": "Improve contact URL discovery and target source quality.",
    "many_sales_rejected": "Refine target/NG rules and do not repeatedly contact rejected categories."
  },
  "agent_output_additions": {
    "expected_appointment_range": "e.g. 0-1 appointments from 1,000 successes; 1 appointment may require around 10,000 successes in ordinary cold outreach",
    "confidence": "low | medium | high",
    "what_to_test_next": ["target segment", "shorter form copy", "LP insertion", "template angle", "follow-up timing"]
  }
}
\`\`\`

### Form Copy Rules

Most company contact forms are not designed for long sales letters. Some have character limits, hidden validation, spam filters, or fields that reject long/URL-heavy content. Default to short form copy and move detail to a URL.

\`\`\`json
{
  "form_copy_policy": {
    "default_length": "250-450 Japanese characters",
    "soft_max": "600 Japanese characters unless the target form clearly allows more",
    "structure": [
      "one short greeting",
      "one sentence explaining why this company is relevant",
      "one concrete offer or benefit",
      "one CTA with a single URL",
      "short signature"
    ],
    "avoid": [
      "long company introduction",
      "multiple URLs",
      "emoji-heavy copy",
      "HTML",
      "excessive punctuation",
      "too many line breaks",
      "abstract AI buzzwords without concrete offer"
    ],
    "required_when_possible": [
      "a concrete URL to LP, material, case study, or offer page",
      "clear next action",
      "sender identity matching the company profile"
    ]
  },
  "when_to_insert_lp": [
    "offer needs explanation longer than a contact form allows",
    "target is broad and needs context before booking",
    "clicks exist but appointments do not happen",
    "copy is getting sent successfully but response rate is weak",
    "the product has proof, examples, pricing, images, or multiple use cases"
  ],
  "lp_role": {
    "form_message": "short hook and URL",
    "lp": "problem, outcome, proof, details, CTA, form",
    "material": "deeper explanation for high-consideration offers"
  }
}
\`\`\`

### Operational Readiness Before Blaming Results

When a user says "it did not send", "thank-you mail did not arrive", "appointments are not coming", or "SalesBreaker is not working", the Agent must first inspect basic operating readiness. Many failures are caused by incomplete profile data, missing URLs, weak follow-up, or insufficient volume.

\`\`\`json
{
  "readiness_checklist": {
    "company_profile": [
      "company name",
      "sender/staff email",
      "last_name / first_name",
      "department / position when available",
      "phone number split into tel1/tel2/tel3",
      "postal code",
      "prefecture/address",
      "homepage URL",
      "representative or staff identity"
    ],
    "sender_readiness": [
      "sender email exists",
      "DNS/SES verification is complete when email delivery is involved",
      "reply/thank-you mail expectations are realistic"
    ],
    "template_readiness": [
      "short form copy",
      "one clear CTA",
      "URL/LP/material/scheduling link included when appropriate",
      "no excessive length",
      "no multiple confusing URLs"
    ],
    "target_readiness": [
      "enough target volume",
      "contact_url quality checked",
      "NG/recent-send/duplicate exclusions checked",
      "target segment matches offer"
    ],
    "followup_readiness": [
      "click logs checked",
      "multiple-click companies prioritized",
      "deal/task/phone follow-up considered",
      "48h and 7d analysis windows scheduled"
    ]
  },
  "common_root_causes": {
    "required_phone_missing": "Many end forms require phone number. If company profile phone is empty, send failures are expected.",
    "required_address_missing": "Some end forms require postal code/address. Empty profile fields can block submit.",
    "missing_url_or_lp": "If the copy has no URL, the prospect has no clear next step.",
    "too_little_volume": "Do not judge appointment performance from tiny send volume.",
    "clicks_without_followup": "Multiple clicks are a sales signal. If no one calls or follows up, that is an operations gap.",
    "calendar_link_noise": "Direct scheduling links can invite low-quality or prank bookings for some offers; use an LP/filtering page when needed."
  },
  "agent_stance": {
    "be_direct": true,
    "do_not_blame_tool_first": true,
    "explain_operational_gap": true,
    "propose_fix_before_complaint": true
  }
}
\`\`\`

Suggested direct but professional wording:

\`\`\`text
現時点では、SalesBreaker側の問題と判断する前に、運用前提を確認した方がよさそうです。

たとえば送信先フォームが電話番号必須なのに、会社概要の電話番号が未入力だと送信できないのは自然です。
同じように、住所・担当者名・送信者メール・DNS確認・URL/LPの有無が不足していると、送信到達や反応率は落ちます。

まず会社概要、送信者設定、文面URL、送付先URL品質、送信量、クリック後フォローを確認しましょう。
\`\`\`

If clicks are present:

\`\`\`text
クリックが複数ついている会社は、かなり優先度が高いです。
メールやフォームだけで放置せず、電話・個別メール・案件化・タスク化のどれかに進めるべきです。
\`\`\`

If scheduling links attract bad submissions:

\`\`\`text
日程調整リンクを直貼りしてイタズラ登録や質の低い予約が増える場合は、いったんLPを挟んで、対象者・課題・申し込み条件を整理してから予約導線に進ませる方が安全です。
\`\`\`

### Suggested Human-Facing Wording

Use wording like this after a job finishes:

\`\`\`text
今回の送信結果は、まず「文面の良し悪し」と「リスト/URL品質」を分けて見るのがよさそうです。

フォーム到達・送信成功は {success_count}/{target_count} 件です。
一方で NO_FORM_FOUND / DEAD_URL / WRONG_FORM_TYPE は、文面ではなく送付先URLやフォーム種別の問題として扱います。

クリックは送信直後にはまだ増えにくいので、まず48時間ほど置いてから反応分析するのが自然です。
現時点でクリック2回以上、または資料/LPクリックがある会社は優先フォロー候補です。

次は以下をおすすめします。
1. NEEDS_REVIEW のサンプル確認
2. NO_FORM_FOUND / DEAD_URL のURL品質改善
3. クリック企業の案件化またはタスク作成
4. 48時間後にテンプレート別・リスト別で再分析
\`\`\`

If the user asks whether the numbers are good:

\`\`\`text
商材・ターゲット・文面でかなり変わりますが、フォーム営業では「成功送信1,000件で1アポならかなり良い」「1万件で1アポでも普通にあり得る」くらいの温度感で見た方が安全です。

なので今回は、アポ数だけで即判断せず、
1. そもそもフォーム到達できているか
2. クリックやLP遷移が出ているか
3. ターゲットと訴求がズレていないか
4. 文面が長すぎず、詳細をLP/資料に逃がせているか
を分けて見ましょう。
\`\`\`

### Do Not Overclaim

- Do not call a campaign successful only because \`SUCCESS\` rows exist.
- Do not call a template bad because \`NO_FORM_FOUND\`, \`DEAD_URL\`, or \`WRONG_FORM_TYPE\` is high.
- Do not judge clicks too early; recommend a later review window.
- Do not merge email fallback results with original form-send results without labeling them.
- Do not create deals, tasks, or exclusions from analysis without clear user intent or confirmation.
- Do not promise appointments from a fixed number of sends. Use benchmark ranges and state uncertainty.
- Do not write long form copy when a short hook plus LP/material URL is more appropriate.

## Never Store

- APIキーの実値。
- 本番環境の秘密情報。
- 巨大な対象ID配列。
- 未承認の実行指示。
- URL query stringに入ったAPIキー。
`,
  },
];

export const findApiKeyDoc = (id) => API_KEY_DOCS.find((doc) => doc.id === id) || API_KEY_DOCS[0];
